import React, { useState, useEffect, useCallback, useRef } from "react";
import Content from "../components/Content";
import Layout from "../containers/LayoutAdministrativo";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Grid,
  Paper,
  Typography,
  Stack,
  Box,
  IconButton,
  Tooltip,
  tooltipClasses,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import Swal from "sweetalert2";
import { mostrarToast } from "../utils/toastUtils";
import { useNavigate, useLocation } from "react-router-dom";
import HistorialEstudiosService from "../services/HistorialEstudiosService";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import ParametrosEstudioService from "../services/ParametrosEstudioService";
import EstudiosIntService from "../services/EstudiosIntService";
import EstudiosService from "../services/EstudiosService";
import SolicitudesEstudiosService from "../services/SolicitudesEstudiosService";
import ResultadosService from "../services/ResultadosService";
import SolicitudesService from "../services/SolicitudesService";
import PacientesService from "../services/PacientesService";
import DoctoresService from "../services/DoctoresService";
import ConveniosService from "../services/ConveniosService";
import MuestrasService from "../services/MuestrasService";
import InventariosService from "../services/InventarioService";
import socketService from "../services/socketService";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ejecutarConCargandoPDF } from "../utils/pdfLoadingUtils";
import headerInformeImg from "../img/header-informeResultados.png";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AssignmentIcon from "@mui/icons-material/Assignment";

const normStr = (s) => String(s || "").trim().toUpperCase();

const isSubEstudioFlag = (estObj) => {
  if (!estObj) return false;
  const val = estObj.registroExamenSub ?? estObj.registro_examen_sub ?? estObj.esSubexamen ?? estObj.isSubEstudio;
  if (val === true || val === 1 || String(val) === "1" || String(val).toUpperCase() === "SI" || String(val).toLowerCase() === "true") {
    return true;
  }
  return false;
};

const parseEdadString = (edadStr) => {
  if (!edadStr) return [0, "AÑOS"];
  const str = String(edadStr).trim();
  const match = str.match(/(\d+(?:\.\d+)?)\s*(AÑOS?|MESES?|DIAS?|DÍAS?|ANOS?)?/i);
  if (match && match[1]) {
    const unidad = match[2] ? match[2].toUpperCase() : "AÑOS";
    return [parseFloat(match[1]), unidad];
  }
  const val = parseFloat(str);
  return [isNaN(val) ? 0 : val, "AÑOS"];
};

const edadEnDias = (valor, unidad) => {
  const v = parseFloat(valor) || 0;
  const u = String(unidad || "AÑOS").toUpperCase();
  if (u.includes("MES")) return v * 30.4375;
  if (u.includes("AÑO") || u.includes("ANO")) return v * 365.25;
  return v; // Días
};

const coincideGenero = (invGenero, pacGenero) => {
  const ig = String(invGenero || "").trim().toLowerCase();
  const pg = String(pacGenero || "").trim().toUpperCase().startsWith("F") ? "F" : "M";
  if (!ig || ig === "ambos" || ig === "ambas" || ig === "todos" || ig === "todas" || ig === "general") return true;
  if (pg === "F") {
    return ig === "mujer" || ig === "mujeres" || ig === "femenino" || ig === "f";
  } else {
    return ig === "hombre" || ig === "hombres" || ig === "masculino" || ig === "m";
  }
};

const obtenerIntervaloCoincidente = (p, pac) => {
  if (!pac) return null;
  const pacGenero = String(pac.sexo || "").trim().toUpperCase().startsWith("F") ? "F" : "M";
  const [edadVal, edadUni] = parseEdadString(pac.edad);
  const pacDias = edadEnDias(edadVal, edadUni);

  const intervals = extractIntervalos(p);
  if (!Array.isArray(intervals) || intervals.length === 0) return null;

  return intervals.find(inv => {
    const gen = inv.genero ?? inv.sexo;
    if (!coincideGenero(gen, pacGenero)) return false;

    const tipoEdad = inv.tipoEdad ?? inv.tipo_edad ?? inv.unidadEdad ?? inv.unidad_edad ?? "AÑOS";
    const rawMin = inv.edadMin ?? inv.edad_min ?? inv.minimoEdad ?? inv.edadMinima;
    const rawMax = inv.edadMax ?? inv.edad_max ?? inv.maximoEdad ?? inv.edadMaxima;

    const minDias = rawMin !== undefined && rawMin !== null && String(rawMin).trim() !== ""
      ? edadEnDias(rawMin, tipoEdad)
      : -Infinity;
    const maxDias = rawMax !== undefined && rawMax !== null && String(rawMax).trim() !== ""
      ? edadEnDias(rawMax, tipoEdad)
      : Infinity;

    return pacDias >= minDias && pacDias <= maxDias;
  }) || null;
};

const appendOptionWithComma = (valorActual, optionValue) => {
  const opt = String(optionValue || "").trim();
  if (!opt) return valorActual || "";
  if (!valorActual || valorActual === "<p><br></p>" || valorActual === "<p></p>") {
    return `<p>${opt}</p>`;
  }

  const trimmed = String(valorActual).trim();

  if (trimmed.endsWith("</p>")) {
    return trimmed.replace(/<\/p>$/, `, ${opt}</p>`);
  }

  return `${trimmed}, ${opt}`;
};

const htmlToPlainText = (raw) => {
  if (!raw) return "";
  let str = String(raw);
  str = str
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<p[^>]*>/gi, "")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/h[1-6]>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<\/li>/gi, "\n")
    .replace(/<\/?ul[^>]*>/gi, "")
    .replace(/<\/?ol[^>]*>/gi, "")
    .replace(/<\/?(?:strong|b|em|i|u|s|span|sub|sup|a|font|mark|small|del|ins|abbr|cite|code|pre|blockquote|table|tr|td|th|thead|tbody|h[1-6]|div)[^>]*>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&deg;/gi, "°")
    .replace(/&plusmn;/gi, "±")
    .replace(/&micro;/gi, "µ")
    .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec));
  return str
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l !== "")
    .join("\n");
};

/**
 * Analiza HTML enriquecido (de ReactQuill) y extrae bloques de párrafos/listas con formato
 * (negrita, cursiva, subrayado).
 */
const parseHtmlToStyledBlocks = (html) => {
  if (!html || typeof html !== "string") return [];
  const trimmed = html.trim();
  if (!trimmed || trimmed === "<p><br></p>" || trimmed === "<p></p>") return [];

  if (typeof window === "undefined" || !window.DOMParser) {
    const plain = htmlToPlainText(html);
    return plain.split("\n").filter(Boolean).map(line => [{ text: line, bold: false, italic: false, underline: false }]);
  }

  const parser = new DOMParser();
  const docHtml = parser.parseFromString(html, "text/html");
  const body = docHtml.body;
  if (!body) return [];

  const blocks = [];

  const extractInlineSpans = (node, styleState = { bold: false, italic: false, underline: false }, spans = []) => {
    if (!node) return spans;

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text) {
        spans.push({
          text,
          bold: Boolean(styleState.bold),
          italic: Boolean(styleState.italic),
          underline: Boolean(styleState.underline)
        });
      }
      return spans;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toUpperCase();

      if (tag === "BR") {
        spans.push({ text: "\n", isLineBreak: true });
        return spans;
      }

      const isBold = Boolean(styleState.bold || tag === "STRONG" || tag === "B" || (node.style && (node.style.fontWeight === "bold" || parseInt(node.style.fontWeight, 10) >= 600)));
      const isItalic = Boolean(styleState.italic || tag === "EM" || tag === "I" || (node.style && node.style.fontStyle === "italic"));
      const isUnderline = Boolean(styleState.underline || tag === "U" || (node.style && String(node.style.textDecoration).includes("underline")));

      const newStyle = {
        bold: isBold,
        italic: isItalic,
        underline: isUnderline
      };

      for (const child of node.childNodes) {
        extractInlineSpans(child, newStyle, spans);
      }
    }

    return spans;
  };

  function splitSpansByNewlines(spans) {
    const result = [];
    let current = [];
    for (const s of spans) {
      if (s.isLineBreak || s.text.includes("\n")) {
        const parts = s.text.split("\n");
        for (let i = 0; i < parts.length; i++) {
          if (parts[i]) {
            current.push({ ...s, text: parts[i], isLineBreak: false });
          }
          if (i < parts.length - 1) {
            if (current.length > 0) result.push(current);
            current = [];
          }
        }
      } else {
        current.push(s);
      }
    }
    if (current.length > 0) result.push(current);
    return result;
  }

  const processBlockNode = (node) => {
    if (!node) return;
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toUpperCase();

      if (tag === "UL") {
        for (const li of node.children) {
          if (li.tagName && li.tagName.toUpperCase() === "LI") {
            const spans = [{ text: "•  ", bold: true, italic: false, underline: false }];
            extractInlineSpans(li, { bold: false, italic: false, underline: false }, spans);
            splitSpansByNewlines(spans).forEach(b => { if (b.length > 0) blocks.push(b); });
          }
        }
        return;
      }

      if (tag === "OL") {
        let count = 1;
        for (const li of node.children) {
          if (li.tagName && li.tagName.toUpperCase() === "LI") {
            const spans = [{ text: `${count}.  `, bold: true, italic: false, underline: false }];
            extractInlineSpans(li, { bold: false, italic: false, underline: false }, spans);
            splitSpansByNewlines(spans).forEach(b => { if (b.length > 0) blocks.push(b); });
            count++;
          }
        }
        return;
      }

      if (["P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6"].includes(tag)) {
        const spans = extractInlineSpans(node, { bold: false, italic: false, underline: false }, []);
        splitSpansByNewlines(spans).forEach(b => { if (b.length > 0) blocks.push(b); });
        return;
      }
    }

    const spans = extractInlineSpans(node, { bold: false, italic: false, underline: false }, []);
    splitSpansByNewlines(spans).forEach(b => { if (b.length > 0) blocks.push(b); });
  };

  for (const child of body.childNodes) {
    processBlockNode(child);
  }

  return blocks.filter(b => b.some(span => span.text && span.text.trim().length > 0));
};

const renderRichTextInPdf = (doc, blocks, startX, startY, maxWidth = 190, maxY = 255, topMargin = 14, fontSize = 9.0, lineHeight = 4.5) => {
  let finalY = startY;

  const getFontStyle = (bold, italic) => {
    if (bold && italic) return "bolditalic";
    if (bold) return "bold";
    if (italic) return "italic";
    return "normal";
  };

  for (let bIdx = 0; bIdx < blocks.length; bIdx++) {
    const spans = blocks[bIdx];

    const tokens = [];
    for (const span of spans) {
      const parts = span.text.split(/(\s+)/);
      for (const part of parts) {
        if (part.length > 0) {
          tokens.push({
            text: part,
            isSpace: /^\s+$/.test(part),
            bold: span.bold,
            italic: span.italic,
            underline: span.underline
          });
        }
      }
    }

    const lines = [];
    let currentLine = [];
    let currentLineWidth = 0;

    for (const token of tokens) {
      doc.setFont("helvetica", getFontStyle(token.bold, token.italic));
      doc.setFontSize(fontSize);
      const tokenW = doc.getTextWidth(token.text);

      if (currentLineWidth + tokenW <= maxWidth || currentLine.length === 0) {
        currentLine.push({ ...token, width: tokenW });
        currentLineWidth += tokenW;
      } else {
        if (currentLine.length > 0) {
          lines.push(currentLine);
        }
        if (token.isSpace) {
          currentLine = [];
          currentLineWidth = 0;
        } else {
          currentLine = [{ ...token, width: tokenW }];
          currentLineWidth = tokenW;
        }
      }
    }
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    for (const lineTokens of lines) {
      if (finalY > maxY) {
        doc.addPage();
        finalY = topMargin + 6;
      }

      let curX = startX;
      for (const t of lineTokens) {
        doc.setFont("helvetica", getFontStyle(t.bold, t.italic));
        doc.setFontSize(fontSize);
        doc.setTextColor(0, 0, 0);
        doc.text(t.text, curX, finalY);

        if (t.underline && !t.isSpace) {
          doc.saveGraphicsState();
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.2);
          doc.line(curX, finalY + 0.5, curX + t.width, finalY + 0.5);
          doc.restoreGraphicsState();
        }

        curX += t.width;
      }

      finalY += lineHeight;
    }

    if (bIdx < blocks.length - 1) {
      finalY += 1.0;
    }
  }

  return finalY;
};

const extractIntervalos = (p) => {
  if (!p) return [];
  const parseIfArrayOrJSON = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === "string" && val.trim().startsWith("[")) {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const sources = [
    p.intervalosBr,
    p.intervalosBR,
    p.intervalos,
    p.intervalos_br,
    p.valoresParametro,
    p.valoresReferencia,
    p.valorReferencia,
    p.valor_referencia,
    p.intervaloBiologicoRef
  ];

  for (const src of sources) {
    const res = parseIfArrayOrJSON(src);
    if (res.length > 0) return res;
  }

  return [];
};

const formatIntervaloBR = (intervalo, unidadMedida) => {
  if (intervalo === null || intervalo === undefined) return "";
  if (typeof intervalo === "string" || typeof intervalo === "number") {
    return String(intervalo).trim();
  }

  const getVal = (...keys) => {
    for (const k of keys) {
      if (intervalo[k] !== undefined && intervalo[k] !== null && String(intervalo[k]).trim() !== "") {
        return intervalo[k];
      }
    }
    return "";
  };

  const minVal = getVal("intervaloMin", "intervalo_min", "minimo", "min", "valMin", "val_min");
  const maxVal = getVal("intervaloMax", "intervalo_max", "maximo", "max", "valMax", "val_max");
  const unitStr = unidadMedida || getVal("unidadMedida", "unidad_medida", "um");
  const unit = unitStr ? ` ${unitStr}` : "";

  let rangoStr = "";
  if (minVal !== "" && maxVal !== "") {
    rangoStr = `${minVal} a ${maxVal}${unit}`;
  } else if (minVal !== "") {
    rangoStr = `> ${minVal}${unit}`;
  } else if (maxVal !== "") {
    rangoStr = `< ${maxVal}${unit}`;
  }

  if (!rangoStr) {
    const rawVal = getVal("valor", "valorReferencia", "valor_referencia", "texto", "descripcion");
    if (rawVal) return String(rawVal).trim();
  }

  return rangoStr;
};

const getCleanValorReferencia = (p) => {
  if (!p) return "";
  let val = p.valorReferencia || p.valor_referencia || p.valorPredeterminado || "";
  if (typeof val === "string" && val.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(val);
      if (parsed.valorReferencia) return parsed.valorReferencia;
      if (parsed.valorPredeterminado) return parsed.valorPredeterminado;
    } catch (e) { }
  }
  if (typeof val === "string" && val.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const lines = parsed.map((i) => formatIntervaloBR(i, p.unidadMedida)).filter(Boolean);
        if (lines.length > 0) return lines.join("\n");
      }
    } catch (e) { }
  }
  return val;
};

const formatParametroIntervalos = (p, pac = null) => {
  if (!p) return "";
  const listIntervalos = extractIntervalos(p);

  if (Array.isArray(listIntervalos) && listIntervalos.length > 0) {
    if (pac) {
      const coincidente = obtenerIntervaloCoincidente(p, pac);
      if (coincidente) {
        return formatIntervaloBR(coincidente, p.unidadMedida);
      }
    }
    const lines = listIntervalos.map((i) => formatIntervaloBR(i, p.unidadMedida)).filter(Boolean);
    if (lines.length > 0) {
      return lines.join("\n");
    }
  }
  const cleanVal = getCleanValorReferencia(p);
  const rawText = cleanVal || p.valorReferencia || p.valor_referencia || p.intervaloBiologicoRef || p.valorPredeterminado || "";
  return htmlToPlainText(rawText);
};

const evaluarEstatusValor = (valor, matchingInterval, param = null) => {
  if (valor === null || valor === undefined) return "NORMAL";
  const strVal = String(valor).trim().replace(",", ".");
  if (strVal === "") return "NORMAL";
  const val = parseFloat(strVal);
  if (isNaN(val)) return "NORMAL";

  let min = NaN;
  let max = NaN;

  if (matchingInterval) {
    const rawMin = matchingInterval.intervaloMin ?? matchingInterval.intervalo_min ?? matchingInterval.minimo ?? matchingInterval.min ?? matchingInterval.valMin;
    const rawMax = matchingInterval.intervaloMax ?? matchingInterval.intervalo_max ?? matchingInterval.maximo ?? matchingInterval.max ?? matchingInterval.valMax;
    if (rawMin !== undefined && rawMin !== null && String(rawMin).trim() !== "") min = parseFloat(rawMin);
    if (rawMax !== undefined && rawMax !== null && String(rawMax).trim() !== "") max = parseFloat(rawMax);
  }

  if (isNaN(min) && isNaN(max) && param) {
    const refStr = formatParametroIntervalos(param);
    if (refStr) {
      const rangeMatch = String(refStr).match(/(\d+(?:\.\d+)?)\s*[-–—aA]\s*(\d+(?:\.\d+)?)/);
      if (rangeMatch) {
        min = parseFloat(rangeMatch[1]);
        max = parseFloat(rangeMatch[2]);
      } else {
        const maxMatch = String(refStr).match(/<=\s*(\d+(?:\.\d+)?)|<\s*(\d+(?:\.\d+)?)/);
        if (maxMatch) {
          max = parseFloat(maxMatch[1] || maxMatch[2]);
        } else {
          const minMatch = String(refStr).match(/>=\s*(\d+(?:\.\d+)?)|>\s*(\d+(?:\.\d+)?)/);
          if (minMatch) {
            min = parseFloat(minMatch[1] || minMatch[2]);
          }
        }
      }
    }
  }

  if (!isNaN(min) && val < min) return "BAJO";
  if (!isNaN(max) && val > max) return "ALTO";
  return "NORMAL";
};

const esParametroBR = (p) => {
  if (!p) return false;
  const tipoRef = String(p.tipoReferencia || p.tipoReferenciaParametro || p.tipoValor || "").toLowerCase().trim();
  if (tipoRef.includes("criterio") || tipoRef.includes("mixto") || tipoRef.includes("texto")) {
    return false;
  }
  if (tipoRef === "intervalobr" || tipoRef === "intervalosbr" || tipoRef === "intervalo") {
    return true;
  }
  const intervalos = extractIntervalos(p);
  return Array.isArray(intervalos) && intervalos.length > 0;
};

const normalizarKeyParam = (str) => {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "")
    .trim();
};

const esParametroCalculadoNombre = (paramName) => {
  const norm = normalizarKeyParam(paramName);
  return (
    norm.includes("bilirrubinaindirecta") ||
    norm === "bindirecta" ||
    (norm.includes("globulina") && !norm.includes("relacion")) ||
    norm.includes("relacionag") ||
    norm.includes("relacionalbuminaglobulina") ||
    (norm.includes("relacion") && norm.includes("ag")) ||
    norm === "colesterolvldl" ||
    norm === "vldlcolesterol" ||
    norm === "vldl" ||
    norm === "colesterolldl" ||
    norm === "ldlcolesterol" ||
    norm === "ldl" ||
    norm.includes("indiceaterogenico") ||
    norm.includes("arteriogenico") ||
    // Volumen corpuscular medio (VCM)
    ((norm.includes("volumen") || norm.includes("vol")) && norm.includes("corpuscular")) ||
    norm === "vcm" ||
    // Concentracion media de hemoglobina corpuscular (CMHC / CHCM / MCHC)
    (norm.includes("concentracion") && (norm.includes("corpuscular") || norm.includes("hemoglobina"))) ||
    norm === "cmhc" ||
    norm === "chcm" ||
    norm === "mchc"
    // norm.includes("nitrogenoureico") ||
    // norm === "bun" ||
    // norm.includes("ureicobun")
  );
};

const recalcularParametrosCalculados = (estudiosYParametros) => {
  if (!Array.isArray(estudiosYParametros) || estudiosYParametros.length === 0) {
    return estudiosYParametros;
  }

  const mapaValores = {};

  // 1. Mapear valores numéricos actuales
  estudiosYParametros.forEach((item) => {
    if (item.type === "parameter" && item.parametro) {
      const nombreNorm = normalizarKeyParam(item.parametro.parametro || item.parametro.nombre);
      const valStr = String(item.resultValue || "").trim();
      const valNum = parseFloat(valStr);
      if (!isNaN(valNum)) {
        mapaValores[nombreNorm] = valNum;
      }
    }
  });

  const getVal = (...keys) => {
    // 1. Coincidencia exacta
    for (const k of keys) {
      const normK = normalizarKeyParam(k);
      if (mapaValores[normK] !== undefined) {
        return mapaValores[normK];
      }
    }
    // 2. Coincidencia parcial cuidadosa
    for (const k of keys) {
      const normK = normalizarKeyParam(k);
      if (!normK) continue;
      for (const mapK in mapaValores) {
        // Evitar falsos positivos: si buscamos hemoglobina pura (hb/hemoglobina), no coincidir con indices corpusculares
        if ((normK === "hemoglobina" || normK === "hb") && mapK.includes("corpuscular")) {
          continue;
        }
        if (mapK === normK || mapK.includes(normK) || normK.includes(mapK)) {
          return mapaValores[mapK];
        }
      }
    }
    return null;
  };

  let actualizados = [...estudiosYParametros];

  for (let pass = 0; pass < 3; pass++) {
    actualizados = actualizados.map((item) => {
      if (item.type !== "parameter" || !item.parametro) return item;

      const pNameNorm = normalizarKeyParam(item.parametro.parametro || item.parametro.nombre);

      let nuevoValorNum = null;
      let esCalculadoTarget = false;

      // 1. BILIRRUBINA INDIRECTA = BILIRRUBINA TOTAL - BILIRRUBINA DIRECTA
      if (pNameNorm.includes("bilirrubinaindirecta") || pNameNorm === "bindirecta") {
        esCalculadoTarget = true;
        const total = getVal("bilirrubinatotal", "btotal");
        const directa = getVal("bilirrubinadirecta", "bdirecta");
        if (total !== null && directa !== null) {
          nuevoValorNum = total - directa;
        }
      }

      // 2. GLOBULINA = PROTEINAS TOTALES - ALBUMINA
      else if (pNameNorm.includes("globulina") && !pNameNorm.includes("relacion")) {
        esCalculadoTarget = true;
        const protTot = getVal("proteinastotales", "proteinatotal", "proteinastotal", "ptotales");
        const alb = getVal("albumina");
        if (protTot !== null && alb !== null) {
          nuevoValorNum = protTot - alb;
        }
      }

      // 3. RELACION A/G = ALBUMINA / GLOBULINA
      else if (pNameNorm.includes("relacionag") || pNameNorm.includes("relacionalbuminaglobulina") || (pNameNorm.includes("relacion") && pNameNorm.includes("ag"))) {
        esCalculadoTarget = true;
        const alb = getVal("albumina");
        const glob = getVal("globulina");
        if (alb !== null && glob !== null && glob > 0) {
          nuevoValorNum = alb / glob;
        }
      }

      // 4. COLESTEROL VLDL = TRIGLICERIDOS / 5
      else if (pNameNorm === "colesterolvldl" || pNameNorm === "vldlcolesterol" || pNameNorm === "vldl") {
        esCalculadoTarget = true;
        const trig = getVal("trigliceridos", "triglicerido");
        if (trig !== null) {
          nuevoValorNum = trig / 5;
        }
      }

      // 5. COLESTEROL LDL = COLESTEROL TOTAL - COLESTEROL HDL - COLESTEROL VLDL
      else if (pNameNorm === "colesterolldl" || pNameNorm === "ldlcolesterol" || pNameNorm === "ldl") {
        esCalculadoTarget = true;
        const colTotal = getVal("colesteroltotal", "colesterol");
        const hdl = getVal("colesterolhdl", "hdlcolesterol", "hdl");
        const vldl = getVal("colesterolvldl", "vldlcolesterol", "vldl");
        if (colTotal !== null && hdl !== null && vldl !== null) {
          nuevoValorNum = colTotal - hdl - vldl;
        }
      }

      // 6. INDICE ATEROGENICO = COLESTEROL LDL / COLESTEROL HDL
      else if (pNameNorm.includes("indiceaterogenico") || pNameNorm.includes("arteriogenico")) {
        esCalculadoTarget = true;
        const ldl = getVal("colesterolldl", "ldlcolesterol", "ldl");
        const hdl = getVal("colesterolhdl", "hdlcolesterol", "hdl");
        if (ldl !== null && hdl !== null && hdl > 0) {
          nuevoValorNum = ldl / hdl;
        }
      }

      // 7. VOLUMEN CORPUSCULAR MEDIO = (HEMATOCRITO * 10) / ERITROCITOS
      else if (((pNameNorm.includes("volumen") || pNameNorm.includes("vol")) && pNameNorm.includes("corpuscular")) || pNameNorm === "vcm") {
        esCalculadoTarget = true;
        const hto = getVal("hematocrito", "hto", "hct");
        const eri = getVal("eritrocitos", "eritrocito", "globulosrojos", "hematies", "rbc", "recuentodeeritrocitos");
        if (hto !== null && eri !== null && eri > 0) {
          nuevoValorNum = (hto * 10) / eri;
        }
      }

      // 8. CONCENTRACION MEDIA DE HEMOGLOBINA CORPUSCULAR = (HEMOGLOBINA * 100) / HEMATOCRITO
      else if ((pNameNorm.includes("concentracion") && (pNameNorm.includes("corpuscular") || pNameNorm.includes("hemoglobina"))) || pNameNorm === "cmhc" || pNameNorm === "chcm" || pNameNorm === "mchc") {
        esCalculadoTarget = true;
        const hb = getVal("hemoglobina", "hb", "hgb");
        const hto = getVal("hematocrito", "hto", "hct");
        if (hb !== null && hto !== null && hto > 0) {
          nuevoValorNum = (hb * 100) / hto;
        }
      }

      // 9. NITROGENO UREICO (BUN) = UREA / 2.4
      // else if (pNameNorm.includes("nitrogenoureico") || pNameNorm === "bun" || pNameNorm.includes("ureicobun")) {
      //   esCalculadoTarget = true;
      //   const urea = getVal("urea");
      //   if (urea !== null) {
      //     nuevoValorNum = urea / 2.4;
      //   }
      // }

      if (esCalculadoTarget) {
        let valFormatted = item.resultValue || "";
        if (nuevoValorNum !== null && !isNaN(nuevoValorNum)) {
          valFormatted = String(Math.round(nuevoValorNum * 100) / 100);
          mapaValores[pNameNorm] = nuevoValorNum;
        }

        return {
          ...item,
          resultValue: valFormatted,
          isCalculado: true,
          validationStatus: evaluarEstatusValor(valFormatted, item.matchingInterval, item.parametro)
        };
      }

      return item;
    });
  }

  return actualizados;
};

const CapturaResultados = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { solicitud, estudio } = location.state || {};

  const [viewOnly, setViewOnly] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [estudiosYParametros, setEstudiosYParametros] = useState([]);
  const [estudioDetalle, setEstudioDetalle] = useState(null);
  const [estatusEstudio, setEstatusEstudio] = useState("P");
  const [observaciones, setObservaciones] = useState("");
  const observacionesRef = useRef("");
  const debounceObsTimerRef = useRef(null);

  const esPrimeraVezRef = useRef(true);
  const estatusActualRef = useRef(estudio?.estatus || null);

  const obtenerEstudioPadreEfectivo = async (idSol, idEstTarget) => {
    if (!idSol || !idEstTarget) return idEstTarget;
    try {
      const resSolEst = await SolicitudesEstudiosService.getEstudiosPorSolicitud(idSol);
      const estudiosEnSolicitud = resSolEst?.estudios || resSolEst?.data || (Array.isArray(resSolEst) ? resSolEst : []);

      for (const estSol of estudiosEnSolicitud) {
        const parentId = estSol.idEstudio || estSol.id_estudio;
        if (!parentId) continue;

        if (String(parentId) === String(idEstTarget)) {
          return parentId;
        }

        const subEsts = (await EstudiosIntService.getEstudiosPorEstudio(parentId)) || [];
        const esSub = subEsts.some(s => String(s.idEstudioSec || s.idEstudio) === String(idEstTarget));
        if (esSub) {
          return parentId;
        }
      }
    } catch (err) {
      console.warn("Error al buscar estudio padre en solicitud:", err);
    }
    return idEstTarget;
  };

  const procesarSalidaInventarioAutomatica = async (idSol, idEst) => {
    if (!idSol || !idEst) return;

    try {
      // Buscar si idEst pertenece a un estudio padre solicitado en esta solicitud
      const idPadreEfectivo = await obtenerEstudioPadreEfectivo(idSol, idEst);

      const sucursalId = currentU?.sucursal || 1;
      const resInv = await InventariosService.getInventario(sucursalId);
      const articulos = resInv?.articulos || [];

      // Helper para obtener artículos vinculados a un idEstudio determinado
      const obtenerArticulosEstudio = (targetIdEst) => {
        const vinculados = [];
        articulos.forEach(art => {
          let consumoVal = 0;
          if (art.relacionesEstudios && art.relacionesEstudios[targetIdEst] !== undefined) {
            consumoVal = art.relacionesEstudios[targetIdEst];
          } else if (art.relacionesEstudios && art.relacionesEstudios[String(targetIdEst)] !== undefined) {
            consumoVal = art.relacionesEstudios[String(targetIdEst)];
          } else if (art.estudiosConsumo && art.estudiosConsumo[targetIdEst] !== undefined) {
            consumoVal = art.estudiosConsumo[targetIdEst];
          } else if (art.estudiosConsumo && art.estudiosConsumo[String(targetIdEst)] !== undefined) {
            consumoVal = art.estudiosConsumo[String(targetIdEst)];
          } else if (Array.isArray(art.relaciones)) {
            const rel = art.relaciones.find(r => String(r.idEstudio) === String(targetIdEst));
            if (rel) consumoVal = rel.consumo;
          }

          if (parseFloat(consumoVal) > 0) {
            vinculados.push({
              articulo: art,
              consumo: parseFloat(consumoVal)
            });
          }
        });
        return vinculados;
      };

      // Artículos del estudio padre efectivo
      const articulosPadre = obtenerArticulosEstudio(idPadreEfectivo);

      // Obtener estudiosInt (sub-estudios integrados del estudio padre)
      const subEstudios = (await EstudiosIntService.getEstudiosPorEstudio(idPadreEfectivo)) || [];
      const tieneSubEstudios = subEstudios.length > 0;

      const salidasADebitar = new Map(); // key: `${idArticulo}_${idEstudioTarget}`, value: { idArticulo, cantidad, idEstudioTarget }

      if (!tieneSubEstudios) {
        // CASO A — Estudio directo sin estudiosInt
        articulosPadre.forEach(item => {
          salidasADebitar.set(`${item.articulo.idArticulo}_${idPadreEfectivo}`, {
            idArticulo: item.articulo.idArticulo,
            cantidad: item.consumo,
            idEstudio: idPadreEfectivo
          });
        });
      } else {
        // CASO B y C — Estudio principal con estudiosInt
        let seUsoArticulosDelPadre = false;

        subEstudios.forEach(sub => {
          const subId = sub.idEstudioSec || sub.idEstudio;
          if (!subId) return;

          const articulosSub = obtenerArticulosEstudio(subId);
          const tieneArticulosSub = articulosSub.length > 0;
          const tieneArticulosPadre = articulosPadre.length > 0;

          if (!tieneArticulosSub && tieneArticulosPadre) {
            // CASO B1: el estudioInt NO tiene artículos vinculados -> usar los artículos del estudio principal (padre)
            seUsoArticulosDelPadre = true;
            articulosPadre.forEach(item => {
              salidasADebitar.set(`${item.articulo.idArticulo}_${idPadreEfectivo}`, {
                idArticulo: item.articulo.idArticulo,
                cantidad: item.consumo,
                idEstudio: idPadreEfectivo
              });
            });
          } else if (tieneArticulosSub && tieneArticulosPadre) {
            // CASO C1: Ambos (estudioInt y estudioPadre) tienen artículos vinculados.
            // Al ejecutarse como parte del padre -> SOLO registrar las salidas de los artículos del estudioPadre.
            seUsoArticulosDelPadre = true;
            articulosPadre.forEach(item => {
              salidasADebitar.set(`${item.articulo.idArticulo}_${idPadreEfectivo}`, {
                idArticulo: item.articulo.idArticulo,
                cantidad: item.consumo,
                idEstudio: idPadreEfectivo
              });
            });
          } else if (tieneArticulosSub && !tieneArticulosPadre) {
            // CASO B2 / C2: El estudioInt tiene artículos vinculados pero el padre NO tiene -> registrar salidas de los artículos del estudioInt
            articulosSub.forEach(item => {
              salidasADebitar.set(`${item.articulo.idArticulo}_${subId}`, {
                idArticulo: item.articulo.idArticulo,
                cantidad: item.consumo,
                idEstudio: subId
              });
            });
          }
        });

        // Si el estudio padre tiene artículos propios y ningún subestudio cayó en las reglas anteriores
        if (!seUsoArticulosDelPadre && articulosPadre.length > 0 && salidasADebitar.size === 0) {
          articulosPadre.forEach(item => {
            salidasADebitar.set(`${item.articulo.idArticulo}_${idPadreEfectivo}`, {
              idArticulo: item.articulo.idArticulo,
              cantidad: item.consumo,
              idEstudio: idPadreEfectivo
            });
          });
        }
      }

      // Procesar salidas determinadas
      for (const [key, salida] of salidasADebitar.entries()) {
        await InventariosService.registrarSalidaEstudio({
          idArticulo: salida.idArticulo,
          cantidad: salida.cantidad,
          idEstudio: salida.idEstudio,
          idSolicitud: idSol,
          motivo: `Consumo automático por captura de resultados - Solicitud #${idSol}`,
          idUsuario: currentU?.id || 1,
          idSucursal: sucursalId
        });
      }
    } catch (err) {
      console.warn("No se pudo procesar la salida automática de inventario:", err);
    }
  };
  const [solicitudInfo, setSolicitudInfo] = useState({
    codigo: solicitud?.codigo || solicitud?.orden || "-",
    nombrePaciente: `${solicitud?.nombrePaciente || ""} ${solicitud?.apPaternoPaciente || ""} ${solicitud?.apMaternoPaciente || ""}`.trim() || "-",
    sexo: solicitud?.sexo || "-",
    fechaNacimiento: solicitud?.fechaNacimiento || "-",
    edad: solicitud?.edad || "-",
    nombreDoctor: solicitud?.nombreDoctor || "-",
    nombreConvenio: solicitud?.nombreConvenio || "-",
  });

  useEffect(() => {
    document.title = "Captura de resultados";

    const handleActualizacionResultado = (data) => {
      console.log("[Real-time] Resultado o estado actualizado");
    };

    socketService.on("resultado_actualizado", handleActualizacionResultado);
    socketService.on("solicitud_actualizada", handleActualizacionResultado);

    return () => {
      socketService.off("resultado_actualizado", handleActualizacionResultado);
      socketService.off("solicitud_actualizada", handleActualizacionResultado);
    };
  }, []);

  function mostrarMensaje(mensaje) {
    mostrarToast(mensaje, "success");
    setTimeout(() => {
      navigate(-1);
    }, 1200);
  }

  function mostrarError(mensaje) {
    Swal.fire({
      title: "¡Error!",
      text: mensaje,
      icon: "error",
      confirmButtonText: "Aceptar",
    });
  }

  const usuarioString = sessionStorage.getItem("usuario");
  const currentU = JSON.parse(usuarioString || "{}");

  const esAdmin = Boolean(
    currentU?.esAdmin === true ||
    ["A", "ADMIN", "ADMINISTRADOR"].includes(
      String(
        currentU?.rol ||
        currentU?.tipo ||
        currentU?.rolNombre ||
        currentU?.perfil ||
        currentU?.role ||
        currentU?.puesto ||
        ""
      ).trim().toUpperCase()
    )
  );

  const isReadOnlyUI = estatusEstudio === "L" && !esAdmin;

  useEffect(() => {
    if (!solicitud || !estudio) {
      mostrarError("No se proporcionaron los datos de la solicitud o del estudio.");
      setCargando(false);
      return;
    }

    setEstatusEstudio(estudio.estatus || "P");
    setObservaciones("");
    observacionesRef.current = "";
    if (debounceObsTimerRef.current) clearTimeout(debounceObsTimerRef.current);

    const fetchDatos = async () => {
      try {
        setCargando(true);

        // 0. Consultar estatus real actualizado del estudio desde el backend (evita desincronía al refrescar con F5)
        try {
          const idSol = solicitud?.idSolicitud || solicitud?.id;
          const idEst = estudio?.idEstudio || estudio?.id;
          if (idSol && idEst) {
            const resSolEst = await SolicitudesEstudiosService.getEstudiosPorSolicitud(idSol);
            const listaEst = resSolEst?.estudios || resSolEst?.data || (Array.isArray(resSolEst) ? resSolEst : []);
            const matchEst = listaEst.find(e => String(e.idEstudio) === String(idEst));
            if (matchEst && matchEst.estatus) {
              setEstatusEstudio(matchEst.estatus);
              estatusActualRef.current = matchEst.estatus;
            }
          }
        } catch (eEst) {
          console.warn("No se pudo obtener el estatus actualizado del estudio:", eEst);
        }

        // 0. Cargar detalles completos de la solicitud, paciente, doctor y convenio
        let updatedSolicitudInfo = {
          codigo: solicitud?.codigo || solicitud?.orden || "-",
          nombrePaciente: `${solicitud?.nombrePaciente || ""} ${solicitud?.apPaternoPaciente || ""} ${solicitud?.apMaternoPaciente || ""}`.trim() || "-",
          sexo: solicitud?.sexo || "-",
          fechaNacimiento: solicitud?.fechaNacimiento || "-",
          edad: solicitud?.edad || "-",
          nombreDoctor: solicitud?.nombreDoctor || "-",
          nombreConvenio: solicitud?.nombreConvenio || "-",
        };

        let pacCompat = {
          sexo: solicitud?.sexo || "",
          edad: solicitud?.edad || "",
        };

        try {
          const resSolicitudes = await SolicitudesService.getSolicitudes();
          const listaSolicitudes = resSolicitudes?.data?.solicitudes || [];
          const solicitudCompleta = listaSolicitudes.find(s => 
            String(s.idSolicitud) === String(solicitud.idSolicitud || solicitud.id) ||
            String(s.codigo) === String(solicitud.codigo || solicitud.orden) ||
            String(s.orden) === String(solicitud.orden || solicitud.codigo)
          );

          if (solicitudCompleta) {
            updatedSolicitudInfo.codigo = solicitudCompleta.codigo || solicitudCompleta.orden || updatedSolicitudInfo.codigo;

            const tareas = [];

            // 1. Consultar datos del Paciente
            if (solicitudCompleta.idPaciente) {
              tareas.push(
                PacientesService.getPacientePorId(solicitudCompleta.idPaciente)
                  .then(resPaciente => {
                    const paciente = resPaciente?.paciente?.[0] || resPaciente?.paciente || resPaciente;
                    if (paciente) {
                      updatedSolicitudInfo.nombrePaciente = `${paciente.nombre || ""} ${paciente.apPaterno || ""} ${paciente.apMaterno || ""}`.trim() || updatedSolicitudInfo.nombrePaciente;
                      let sexoStr = paciente.sexo || "-";
                      if (sexoStr === "M") {
                        sexoStr = "Masculino";
                      } else if (sexoStr === "F") {
                        sexoStr = "Femenino";
                      }
                      updatedSolicitudInfo.sexo = sexoStr;
                      updatedSolicitudInfo.fechaNacimiento = paciente.fechaNacimiento || "-";
                      updatedSolicitudInfo.edad = paciente.edad || "-";

                      pacCompat.sexo = paciente.sexo;
                      pacCompat.edad = paciente.edad;
                    }
                  })
                  .catch(ePac => console.warn("Error cargando datos del paciente:", ePac))
              );
            }

            // 2. Consultar datos del Doctor (en paralelo si hay múltiples)
            const idsDocs = solicitudCompleta.idDoctores || (solicitudCompleta.idDoctor ? [solicitudCompleta.idDoctor] : []);
            if (idsDocs.length > 0) {
              tareas.push(
                Promise.all(
                  idsDocs.map(idDoc => idDoc ? DoctoresService.getDoctorPorId(idDoc).catch(() => null) : null)
                ).then(docsRes => {
                  const nombresDoctores = docsRes
                    .filter(Boolean)
                    .map(resDoctor => {
                      const doctor = resDoctor?.doctor?.[0] || resDoctor?.doctor || resDoctor;
                      return doctor ? `${doctor.nombre || ""} ${doctor.apPaterno || ""} ${doctor.apMaterno || ""}`.trim() : null;
                    })
                    .filter(Boolean);
                  if (nombresDoctores.length > 0) {
                    updatedSolicitudInfo.nombreDoctor = nombresDoctores.join(" / ");
                  } else {
                    updatedSolicitudInfo.nombreDoctor = "DOCTOR";
                  }
                })
              );
            } else {
              updatedSolicitudInfo.nombreDoctor = "DOCTOR";
            }

            // 3. Consultar datos del Convenio
            if (solicitudCompleta.idConvenio) {
              tareas.push(
                ConveniosService.getConvenioPorId(solicitudCompleta.idConvenio)
                  .then(resConvenio => {
                    const convenio = resConvenio?.convenio?.[0] || resConvenio?.convenio || resConvenio;
                    if (convenio && convenio.nombre) {
                      updatedSolicitudInfo.nombreConvenio = convenio.nombre;
                    }
                  })
                  .catch(eConv => console.warn("Error cargando convenio:", eConv))
              );
            }

            await Promise.all(tareas);
          }
        } catch (eDetails) {
          console.warn("No se pudieron cargar todos los detalles de la solicitud:", eDetails);
        }

        setSolicitudInfo(updatedSolicitudInfo);

        // 0. Obtener detalle completo del estudio principal desde la base de datos
        try {
          const mainEstDet = await EstudiosService.getEstudioPorId(estudio.idEstudio);
          if (mainEstDet) {
            setEstudioDetalle(mainEstDet);
          }
        } catch (eEstDet) {
          console.warn("No se pudo obtener detalle del estudio principal:", eEstDet);
        }

        // 1. Obtener parámetros directos del estudio principal
        const paramsPrincipales = await ParametrosEstudioService.getParametrosPorEstudio(estudio.idEstudio) || [];

        // 2. Definir función de carga recursiva de sub-exámenes
        const cargarSubEstudiosRecursivo = async (idEstudioPadre) => {
          const resInt = await EstudiosIntService.getEstudiosPorEstudio(idEstudioPadre) || [];
          const list = [];
          for (const sub of resInt) {
            try {
              const detalleEstudio = await EstudiosService.getEstudioPorId(sub.idEstudioSec);
              if (detalleEstudio) {
                const paramsSub = await ParametrosEstudioService.getParametrosPorEstudio(sub.idEstudioSec) || [];
                const hijos = await cargarSubEstudiosRecursivo(sub.idEstudioSec);
                list.push({
                  estudio: detalleEstudio,
                  parametros: paramsSub,
                  hijos: hijos
                });
              }
            } catch (errSub) {
              console.error("Error loading nested sub-study:", errSub);
            }
          }
          return list;
        };

        const subEstudiosJerarquia = await cargarSubEstudiosRecursivo(estudio.idEstudio);

        // 3. Obtener resultados ya guardados para esta solicitud en la BD
        let resultadosGuardados = [];
        try {
          const resResultados = await ResultadosService.getResultadosPorSolicitud(solicitud.idSolicitud);
          if (resResultados.estatus === "OK") {
            resultadosGuardados = resResultados.resultados || [];
          }
        } catch (e) {
          console.warn("No se pudieron consultar resultados previos (puede que el endpoint no exista aún):", e);
        }

        // Recopilar el ID del estudio principal efectivo y todos los IDs de sus sub-estudios integrados
        const idPadreEfectivoCheck = await obtenerEstudioPadreEfectivo(solicitud?.idSolicitud, estudio?.idEstudio);
        const subEstudiosJerarquiaCheck = (String(idPadreEfectivoCheck) === String(estudio?.idEstudio))
          ? subEstudiosJerarquia
          : await cargarSubEstudiosRecursivo(idPadreEfectivoCheck);

        const idsEstudioYSubEstudios = new Set([String(idPadreEfectivoCheck)]);
        const recolectarIdsSub = (lista) => {
          (lista || []).forEach(item => {
            if (item.estudio?.idEstudio) {
              idsEstudioYSubEstudios.add(String(item.estudio.idEstudio));
            }
            if (item.hijos && item.hijos.length > 0) {
              recolectarIdsSub(item.hijos);
            }
          });
        };
        recolectarIdsSub(subEstudiosJerarquiaCheck);

        // Buscar observaciones de esta tabla:
        // Si no son subexámenes (vienen en una sola tabla), comparten la observación del estudio principal/padre.
        // Si sí son subexámenes (tablas separadas), cada subexamen tiene su propia observación.
        const idsMismaTabla = new Set([String(estudio?.idEstudio || estudio?.id)]);
        const recolectarIdsNoSub = (lista) => {
          (lista || []).forEach(item => {
            const subEst = item.estudio || {};
            if (!isSubEstudioFlag(subEst)) {
              if (subEst.idEstudio) idsMismaTabla.add(String(subEst.idEstudio));
              if (item.hijos && item.hijos.length > 0) {
                recolectarIdsNoSub(item.hijos);
              }
            }
          });
        };
        recolectarIdsNoSub(subEstudiosJerarquia);

        const resMismaTabla = (resultadosGuardados || []).filter(r => {
          const rEstId = String(r.idEstudio || r.id_estudio || "");
          return idsMismaTabla.has(rEstId);
        });

        let resultadoConObs = resMismaTabla.find(
          r => String(r.idEstudio || r.id_estudio || "") === String(estudio?.idEstudio || estudio?.id) &&
               r.observaciones && String(r.observaciones).trim() !== ""
        );
        if (!resultadoConObs) {
          resultadoConObs = resMismaTabla.find(r => r.observaciones && String(r.observaciones).trim() !== "");
        }

        if (resultadoConObs) {
          setObservaciones(resultadoConObs.observaciones);
          observacionesRef.current = resultadoConObs.observaciones;
        } else {
          setObservaciones("");
          observacionesRef.current = "";
        }

        const yaTieneResultadosEsteEstudio = (resultadosGuardados || []).some(r => {
          const rEstId = String(r.idEstudio || r.id_estudio || "");
          const rVal = r.valor;
          const esDeEsteEstudioOIntegrado = idsEstudioYSubEstudios.has(rEstId);
          const tieneValor = rVal !== null && rVal !== undefined && String(rVal).trim() !== "";
          return esDeEsteEstudioOIntegrado && tieneValor;
        });

        esPrimeraVezRef.current = !yaTieneResultadosEsteEstudio;

        const resultMap = {};
        resultadosGuardados.forEach(r => {
          const pId = r.idParametrosEstudio || r.idParametro || r.id_parametros_estudio || r.id_parametro;
          const eId = r.idEstudio || r.id_estudio;
          const valObj = {
            valor: r.valor,
            verificado: r.verificado === true || r.verificado === 1 || String(r.verificado) === "1" || String(r.verificado).toLowerCase() === "true",
            idResultado: r.idResultado || r.id
          };
          if (eId && pId) resultMap[`${eId}_${pId}`] = valObj;
          if (pId) resultMap[`${pId}`] = valObj;
        });

        // 4. Construir lista plana de filas para la tabla
        const flatList = [];

        // Fila principal del estudio solicitado
        flatList.push({
          type: "studyHeader",
          idEstudio: estudio.idEstudio,
          nombre: estudio.nombre,
          isSubEstudio: false,
          nivel: 0
        });

        // Parámetros directos del estudio principal
        paramsPrincipales.forEach(p => {
          const pId = p.idParametrosEstudio || p.idParametro || p.id || p.id_parametros_estudio;
          const guardado = resultMap[`${estudio.idEstudio}_${pId}`] || resultMap[`${pId}`];
          const valorGuardado = guardado?.valor || "";
          const verificadoGuardado = guardado?.verificado !== undefined
            ? guardado.verificado
            : (p.verificado === true || p.verificado === 1 || String(p.verificado) === "1" || String(p.verificado).toLowerCase() === "true");
          const idResultado = guardado?.idResultado || "";
          const matchingInterval = obtenerIntervaloCoincidente(p, pacCompat);
          const validationStatus = evaluarEstatusValor(valorGuardado, matchingInterval, p);

          flatList.push({
            type: "parameter",
            idEstudio: estudio.idEstudio,
            parametro: p,
            resultValue: valorGuardado,
            verificado: verificadoGuardado,
            idResultado: idResultado,
            matchingInterval,
            validationStatus,
            nivel: 0,
            isCalculado: esParametroCalculadoNombre(p.parametro || p.nombre)
          });
        });

        // 5. Agregar recursivamente sub-estudios y sus parámetros
        const aplanarYAgregarSubEstudios = (listaSubEstudios, nivel = 1) => {
          listaSubEstudios.forEach(sub => {
            const subEst = sub.estudio || {};
            const esSubFlag = isSubEstudioFlag(subEst);
            const subParams = sub.parametros || [];
            const esMismoNombreUnicoParam =
              !esSubFlag &&
              subParams.length === 1 &&
              normStr(subEst.nombre) === normStr(subParams[0]?.parametro || subParams[0]?.nombre);

            if (!esMismoNombreUnicoParam) {
              flatList.push({
                type: "studyHeader",
                idEstudio: subEst.idEstudio,
                nombre: subEst.nombre,
                isSubEstudio: true,
                nivel: nivel
              });
            }

            subParams.forEach(p => {
              const pId = p.idParametrosEstudio || p.idParametro || p.id || p.id_parametros_estudio;
              const guardado = resultMap[`${subEst.idEstudio}_${pId}`] || resultMap[`${pId}`];
              const valorGuardado = guardado?.valor || "";
              const verificadoGuardado = guardado?.verificado !== undefined
                ? guardado.verificado
                : (p.verificado === true || p.verificado === 1 || String(p.verificado) === "1" || String(p.verificado).toLowerCase() === "true");
              const idResultado = guardado?.idResultado || null;
              const matchingInterval = obtenerIntervaloCoincidente(p, pacCompat);
              const validationStatus = evaluarEstatusValor(valorGuardado, matchingInterval, p);

              flatList.push({
                type: "parameter",
                idEstudio: subEst.idEstudio,
                parametro: p,
                resultValue: valorGuardado,
                verificado: verificadoGuardado,
                idResultado: idResultado,
                matchingInterval,
                validationStatus,
                nivel: nivel,
                isCalculado: esParametroCalculadoNombre(p.parametro || p.nombre)
              });
            });

            if (sub.hijos && sub.hijos.length > 0) {
              aplanarYAgregarSubEstudios(sub.hijos, nivel + 1);
            }
          });
        };

        aplanarYAgregarSubEstudios(subEstudiosJerarquia, 1);

        setEstudiosYParametros(recalcularParametrosCalculados(flatList));
      } catch (err) {
        console.error(err);
        mostrarError("Error al cargar la estructura de parámetros del estudio.");
      } finally {
        setCargando(false);
      }
    };

    fetchDatos();
  }, [solicitud, estudio, navigate]);

  const handleValueChange = (paramIndex, value) => {
    setEstudiosYParametros(prev => {
      const nuevos = [...prev];
      const item = { ...nuevos[paramIndex] };
      item.resultValue = value;
      item.validationStatus = evaluarEstatusValor(value, item.matchingInterval, item.parametro);
      nuevos[paramIndex] = item;
      return recalcularParametrosCalculados(nuevos);
    });
  };

  const handleSelectOptionMixto = (paramIndex, optionValue) => {
    if (!optionValue) return;
    let listaActualizada = [];
    setEstudiosYParametros(prev => {
      listaActualizada = [...prev];
      const item = { ...listaActualizada[paramIndex] };
      const valorActual = item.resultValue || "";
      item.resultValue = appendOptionWithComma(valorActual, optionValue);
      item.validationStatus = evaluarEstatusValor(item.resultValue, item.matchingInterval, item.parametro);
      listaActualizada[paramIndex] = item;
      return listaActualizada;
    });
    autoGuardar(estatusEstudio, observaciones, listaActualizada);
  };

  const handleVerificadoChange = (paramIndex, isChecked) => {
    let listaActualizada = [];
    setEstudiosYParametros(prev => {
      listaActualizada = prev.map((item, idx) =>
        idx === paramIndex ? { ...item, verificado: isChecked } : item
      );
      return listaActualizada;
    });
    autoGuardar(estatusEstudio, observaciones, listaActualizada);
  };

  const handleToggleAllVerificado = () => {
    let listaActualizada = [];
    setEstudiosYParametros(prev => {
      const parameterItems = prev.filter(item => item.type === "parameter");
      const allChecked = parameterItems.length > 0 && parameterItems.every(item => item.verificado);
      const newChecked = !allChecked;
      listaActualizada = prev.map(item => {
        if (item.type === "parameter") {
          return { ...item, verificado: newChecked };
        }
        return item;
      });
      return listaActualizada;
    });
    autoGuardar(estatusEstudio, observaciones, listaActualizada);
  };

  const guardarResultados = async () => {
    try {
      const payloadResultados = estudiosYParametros
        .filter(item => item.type === "parameter")
        .map(item => {
          const resObj = {
            idEstudio: item.idEstudio,
            idParametrosEstudio: item.parametro.idParametrosEstudio,
            valor: item.resultValue || "",
            verificado: item.verificado ? true : false,
            observaciones: observaciones || "",
            idUsuario: currentU?.id
          };
          if (item.idResultado) {
            resObj.idResultado = item.idResultado;
          }
          return resObj;
        });

      const payload = {
        idSolicitud: solicitud.idSolicitud,
        resultados: payloadResultados
      };

      // 1. Guardar resultados
      const res = await ResultadosService.agregarResultados(payload);

      if (res.data.estatus === "OK") {
        // Descontar inventario automáticamente solo la primera vez tras respuesta OK 200 y cuando haya al menos un valor capturado
        if (esPrimeraVezRef.current) {
          const algunValorCapturado = payloadResultados.some(r => {
            const v = r.valor;
            return v !== null && v !== undefined && String(v).trim() !== "" && v !== "<p><br></p>" && v !== "<p></p>";
          });
          if (algunValorCapturado) {
            esPrimeraVezRef.current = false;
            await procesarSalidaInventarioAutomatica(solicitud?.idSolicitud, estudio?.idEstudio);
          }
        }

        // 2. Actualizar estatus del estudio en solicitudesEstudios
        let estatus = {
          idSolicitud: solicitud.idSolicitud,
          idEstudio: estudio.idEstudio,
          idEstudioPadre: estudio.idEstudioPadre || null,
          estatus: estatusEstudio
        }
        try {
          await SolicitudesEstudiosService.updateEstatusEstudio(estatus, estudio.idEstudio);
          if (estatusActualRef.current !== estatusEstudio) {
            HistorialEstudiosService.registrarMovimiento({
              idSolicitud: solicitud.idSolicitud,
              idEstudio: estudio.idEstudioPadre || estudio.idEstudio,
              nombreEstudio: estudio.nombre || estudio.nombreEstudio || "",
              nombreEmpleado: currentU?.nombre || "EMPLEADO",
              tipoAccion: estatusEstudio,
            });
            estatusActualRef.current = estatusEstudio;
          }
        } catch (eStatus) {
          console.warn("No se pudo actualizar el estatus de la solicitud:", eStatus);
        }

        mostrarMensaje("¡Resultados guardados con éxito!");
      } else {
        mostrarError(res.data.mensaje || "Error al guardar resultados.");
      }
    } catch (err) {
      console.error(err);
      mostrarError("No se pudo guardar la información de resultados en el servidor.");
    }
  };

  const handleLiberarClick = () => {
    // Condición 6: No se podrá liberar un estudio hasta que todos los parámetros tengan algún valor.
    const parametrosSinValor = estudiosYParametros
      .filter(item => item.type === "parameter")
      .filter(item => {
        const val = item.resultValue;
        return !val || String(val).trim() === "" || val === "<p><br></p>" || val === "<p></p>";
      });

    if (parametrosSinValor.length > 0) {
      mostrarError("Para poder liberar el estudio, todos los parámetros deben tener un resultado capturado.");
      return;
    }

    setEstatusEstudio("L");
    const obsFinal = (observacionesRef.current !== undefined && observacionesRef.current !== null) ? observacionesRef.current : observaciones;
    autoGuardar("L", obsFinal, null, true);
  };

  const autoGuardar = async (nuevoEstatus = estatusEstudio, nuevaObs = undefined, customLista = null, forceSave = false) => {
    try {
      // Condición 5: Si fue liberado y no es Admin, ya no se aceptarán cambios automáticos al perder el foco
      if (estatusEstudio === "L" && !forceSave && !esAdmin) {
        return;
      }

      const obsAGuardar = (nuevaObs !== undefined && nuevaObs !== null)
        ? nuevaObs
        : ((observacionesRef.current !== undefined && observacionesRef.current !== null) ? observacionesRef.current : observaciones);

      const listaParaGuardar = customLista || estudiosYParametros;
      const payloadResultados = listaParaGuardar
        .filter(item => item.type === "parameter")
        .map(item => {
          const p = item.parametro || {};
          const pId = p.idParametrosEstudio || p.idParametro || p.id || p.id_parametros_estudio;
          const resObj = {
            idEstudio: item.idEstudio || estudio.idEstudio,
            idParametrosEstudio: pId,
            valor: item.resultValue || "",
            verificado: item.verificado ? true : false,
            observaciones: obsAGuardar || "",
            idUsuario: currentU?.id
          };
          if (item.idResultado) {
            resObj.idResultado = item.idResultado;
          }
          return resObj;
        });

      const payload = {
        idSolicitud: solicitud.idSolicitud,
        resultados: payloadResultados
      };

      // 1. Guardar resultados de forma silenciosa
      const res = await ResultadosService.agregarResultados(payload);

      if (res.data.estatus === "OK") {
        // Descontar inventario automáticamente solo la primera vez tras respuesta OK 200 y cuando haya al menos un valor capturado
        if (esPrimeraVezRef.current) {
          const algunValorCapturado = payloadResultados.some(r => {
            const v = r.valor;
            return v !== null && v !== undefined && String(v).trim() !== "" && v !== "<p><br></p>" && v !== "<p></p>";
          });
          if (algunValorCapturado) {
            esPrimeraVezRef.current = false;
            await procesarSalidaInventarioAutomatica(solicitud?.idSolicitud, estudio?.idEstudio);
          }
        }
        // Refrescamos los IDs recién creados en la BD para que las siguientes ediciones 
        // editen el registro existente en lugar de duplicarlo
        try {
          const resResultados = await ResultadosService.getResultadosPorSolicitud(solicitud.idSolicitud);
          if (resResultados.estatus === "OK") {
            const resultadosGuardados = resResultados.resultados || [];
            const resultMap = {};
            resultadosGuardados.forEach(r => {
              const pId = r.idParametrosEstudio || r.idParametro || r.id_parametros_estudio || r.id_parametro;
              const eId = r.idEstudio || r.id_estudio;
              const valObj = {
                valor: r.valor,
                verificado: r.verificado === true || r.verificado === 1 || String(r.verificado) === "1" || String(r.verificado).toLowerCase() === "true",
                idResultado: r.idResultado || r.id
              };
              if (eId && pId) resultMap[`${eId}_${pId}`] = valObj;
              if (pId) resultMap[`${pId}`] = valObj;
            });

            setEstudiosYParametros(prev =>
              prev.map(item => {
                if (item.type !== "parameter") return item;
                const p = item.parametro || {};
                const pId = p.idParametrosEstudio || p.idParametro || p.id || p.id_parametros_estudio;
                const guardado = resultMap[`${item.idEstudio}_${pId}`] || resultMap[`${pId}`];
                return {
                  ...item,
                  idResultado: guardado?.idResultado || item.idResultado
                };
              })
            );
          }
        } catch (eGet) {
          console.warn("No se pudieron refrescar los IDs de resultados en el autoguardado:", eGet);
        }

        // 2. Actualizar el estatus del estudio
        let estatusData = {
          idSolicitud: solicitud.idSolicitud,
          idEstudio: estudio.idEstudio,
          idEstudioPadre: estudio.idEstudioPadre || null,
          estatus: nuevoEstatus
        };
        try {
          await SolicitudesEstudiosService.updateEstatusEstudio(estatusData, estudio.idEstudio);
          // Condición 2: Si el estudio principal cambia de estatus, todos sus hijos también deben hacerlo
          if (!estudio.idEstudioPadre) {
            try {
              await SolicitudesEstudiosService.updateEstatusEstudiosPorPadre({
                idSolicitud: solicitud.idSolicitud,
                idEstudioPadre: estudio.idEstudio,
                estatus: nuevoEstatus
              });
            } catch (ePadre) { }
          }
          if (estatusActualRef.current !== nuevoEstatus) {
            HistorialEstudiosService.registrarMovimiento({
              idSolicitud: solicitud.idSolicitud,
              idEstudio: estudio.idEstudioPadre || estudio.idEstudio,
              nombreEstudio: estudio.nombre || estudio.nombreEstudio || "",
              nombreEmpleado: currentU?.nombre || "EMPLEADO",
              tipoAccion: nuevoEstatus,
            });
            estatusActualRef.current = nuevoEstatus;
          }
        } catch (eStatus) {
          console.warn("No se pudo actualizar el estatus de la solicitud:", eStatus);
        }
      }
    } catch (err) {
      console.error("Error en autoguardado:", err);
    }
  };

  const handleGenerarInformeResultadosPDF = async () => {
    try {
      if (debounceObsTimerRef.current) clearTimeout(debounceObsTimerRef.current);
      const obsActual = (observacionesRef.current !== undefined && observacionesRef.current !== null)
        ? observacionesRef.current
        : observaciones;
      if (obsActual !== undefined && obsActual !== "") {
        await autoGuardar(estatusEstudio, obsActual, null, true);
      }

      await ejecutarConCargandoPDF(
        async () => {
          const doc = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
          });

          const now = new Date();
          const meses = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
          const dia = String(now.getDate()).padStart(2, '0');
          const mes = meses[now.getMonth()];
          const anio = now.getFullYear();
          const hrs = String(now.getHours()).padStart(2, '0');
          const mins = String(now.getMinutes()).padStart(2, '0');
          const fechaImpresionStr = `${dia}/${mes}/${anio} ${hrs}:${mins} h`;

          const idSol = solicitudInfo.idSolicitud || solicitud?.idSolicitud || solicitud?.id;
          const mainIdEst = estudio?.idEstudio || estudio?.id;

          const pacUpper = String(solicitudInfo?.nombrePaciente || "PACIENTE").trim().replace(/\s+/g, " ").replace(/[\\/:*?"<>|]/g, "").toUpperCase();
          const codSolSafe = String(solicitudInfo?.codigo || idSol || "").trim().replace(/[\\/:*?"<>|]/g, "");
          doc.setProperties({
            title: `${pacUpper}_${codSolSafe}`,
          });

          // 1. Imagen de Encabezado (ajustada a 25.5mm para igualar el ejemplo)
          try {
            doc.addImage(headerInformeImg, "PNG", 10, 7.5, 190, 25.5);
          } catch (eImg) { }

          // 2. Recuadro Datos Paciente y Solicitud
          const startY = 35;
          let doctorText = solicitudInfo.nombreDoctor || "DOCTOR";
          let docsList = [];
          if (doctorText.includes("/")) {
            docsList = doctorText.split("/").map(d => d.trim()).filter(Boolean);
          } else {
            docsList = [doctorText];
          }

          const tieneEmpresa = solicitudInfo.nombreConvenio && solicitudInfo.nombreConvenio !== "-" && String(solicitudInfo.nombreConvenio).trim() !== "";
          const numDocLines = Math.max(1, docsList.length);
          const totalLeftLines = (tieneEmpresa ? 1 : 0) + 3 + numDocLines;
          const boxHeight = Math.max(19, 4 + totalLeftLines * 3.6);

          doc.setDrawColor(14, 72, 113);
          doc.setLineWidth(0.4);
          doc.rect(10, startY, 190, boxHeight);
          doc.line(135, startY, 135, startY + boxHeight);

          // Columna Izquierda (Empresa [opcional], Paciente, Edad, Sexo, Dirigido)
          let currY = startY + 3.6;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7.5);

          if (tieneEmpresa) {
            doc.setTextColor(14, 72, 113);
            doc.text("Nombre de empresa:", 12, currY);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(0, 0, 0);
            doc.text(String(solicitudInfo.nombreConvenio).trim(), 40, currY);
            currY += 3.6;
          }

          doc.setFont("helvetica", "bold");
          doc.setTextColor(14, 72, 113);
          doc.text("Paciente:", 12, currY);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 0, 0);
          doc.text(String(solicitudInfo.nombrePaciente || "-"), 27, currY);

          currY += 3.6;
          doc.setFont("helvetica", "bold");
          doc.setTextColor(14, 72, 113);
          doc.text("Edad:", 12, currY);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 0, 0);
          const edadNacStr = `${solicitudInfo.edad || "-"} ${solicitudInfo.fechaNacimiento && solicitudInfo.fechaNacimiento !== "-" ? `(${solicitudInfo.fechaNacimiento})` : ""}`;
          doc.text(edadNacStr, 23, currY);

          currY += 3.6;
          doc.setFont("helvetica", "bold");
          doc.setTextColor(14, 72, 113);
          doc.text("Sexo:", 12, currY);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 0, 0);
          doc.text(String(solicitudInfo.sexo || "-"), 23, currY);

          currY += 3.6;
          doc.setFont("helvetica", "bold");
          doc.setTextColor(14, 72, 113);
          doc.text("Dirigido:", 12, currY);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 0, 0);

          docsList.forEach((docName, dIdx) => {
            const lineText = `Dr(a). ${docName}`;
            if (dIdx === 0) {
              doc.text(lineText, 25, currY);
            } else {
              currY += 3.6;
              doc.text(lineText, 25, currY);
            }
          });

          // Columna Derecha (No Solicitud, Fecha, Impresión)
          let currRightY = startY + 3.6;
          doc.setFont("helvetica", "bold");
          doc.setTextColor(14, 72, 113);
          doc.text("No. Solicitud:", 137, currRightY);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 0, 0);
          doc.text(String(solicitudInfo.codigo || "-"), 160, currRightY);

          currRightY += 3.8;
          doc.setFont("helvetica", "bold");
          doc.setTextColor(14, 72, 113);
          doc.text("Fecha:", 137, currRightY);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 0, 0);
          doc.text(String(solicitudInfo.fechaSolicitud || solicitud?.fecha || new Date().toLocaleDateString("es-MX")), 150, currRightY);

          currRightY += 3.8;
          doc.setFont("helvetica", "bold");
          doc.setTextColor(14, 72, 113);
          doc.text("Impresión:", 137, currRightY);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 0, 0);
          doc.text(fechaImpresionStr, 154, currRightY);

          // Fecha de muestra primaria
          const yMuestra = startY + boxHeight + 6;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(0, 0, 0);
          doc.text(`Fecha de muestra primaria: ${solicitudInfo.fechaSolicitud || solicitud?.fecha || new Date().toLocaleDateString("es-MX")}`, 10, yMuestra);

          let rawResultados = [];
          try {
            const resRes = await ResultadosService.getResultadosPorSolicitud(idSol);
            if (resRes && resRes.estatus === "OK" && Array.isArray(resRes.resultados)) {
              rawResultados = resRes.resultados;
            }
          } catch (e) { }

          const resultMap = {};
          rawResultados.forEach(r => {
            const key = `${r.idEstudio}-${r.idParametrosEstudio || r.idParametro}`;
            resultMap[key] = r;
          });

          const paramItemsMapByEstudio = {};

          const cargarNodoRecursivo = async (estNodo, idPadre = null) => {
            const idEstNode = estNodo.idEstudio || estNodo.id;
            let detEst = estNodo;
            if (idEstNode) {
              try {
                const fetched = await EstudiosService.getEstudioPorId(idEstNode);
                if (fetched) detEst = { ...estNodo, ...fetched };
              } catch (e) { }
            }

            let tipoM = detEst.tipoMuestra || detEst.muestra || detEst.material || "";
            if (!tipoM && idEstNode) {
              try {
                const resMuestras = await MuestrasService.getMuestrasPorEstudio(idEstNode);
                if (Array.isArray(resMuestras) && resMuestras.length > 0) {
                  tipoM = resMuestras[0].tipoMuestra || resMuestras[0].nombreMaterial || resMuestras[0].material || tipoM;
                }
              } catch (e) { }
            }
            detEst.tipoMuestra = tipoM;

            if (idEstNode && !paramItemsMapByEstudio[idEstNode]) {
              try {
                const pList = await ParametrosEstudioService.getParametrosPorEstudio(idEstNode) || [];
                paramItemsMapByEstudio[idEstNode] = pList.map(p => {
                  const pId = p.idParametrosEstudio || p.id;
                  const rObj = resultMap[`${idEstNode}-${pId}`] || {};
                  return {
                    type: "parameter",
                    idEstudio: idEstNode,
                    parametro: p,
                    resultValue: rObj.valor || "",
                    verificado: rObj.verificado || false,
                    idResultado: rObj.idResultado
                  };
                });
              } catch (e) { }
            }

            let rawInt = [];
            if (idEstNode) {
              try {
                rawInt = await EstudiosIntService.getEstudiosPorEstudio(idEstNode) || [];
              } catch (e) { }
            }
            const hijosNodos = [];
            for (const sub of rawInt) {
              const idSec = sub.idEstudioSec || sub.idEstudio || sub.id_estudio_sec;
              if (idSec) {
                try {
                  const subDet = await EstudiosService.getEstudioPorId(idSec);
                  if (subDet) {
                    const nodeHijo = await cargarNodoRecursivo(subDet, idEstNode);
                    hijosNodos.push(nodeHijo);
                  }
                } catch (e) { }
              }
            }

            return {
              estudio: detEst,
              idPadre: idPadre,
              hijos: hijosNodos,
            };
          };

          const tieneValorParametro = (item) => {
            if (!item) return false;
            const raw = item.resultValue !== undefined ? item.resultValue : (item.valor !== undefined ? item.valor : "");
            if (raw === null || raw === undefined) return false;
            const clean = htmlToPlainText(String(raw)).trim();
            return clean.length > 0;
          };

          const subArbolTieneValoresMismaTabla = (n) => {
            if (!n) return false;
            const cEst = n.estudio || {};
            if (isSubEstudioFlag(cEst)) return false;

            const cId = cEst.idEstudio || cEst.id;
            const cParams = paramItemsMapByEstudio[cId] || [];
            if (cParams.some(p => tieneValorParametro(p))) {
              return true;
            }

            for (const nieto of n.hijos || []) {
              if (!isSubEstudioFlag(nieto.estudio)) {
                if (subArbolTieneValoresMismaTabla(nieto)) {
                  return true;
                }
              }
            }
            return false;
          };

          const generarTablasPDF = (node) => {
            const tablas = [];
            const est = node.estudio || {};
            const idE = est.idEstudio || est.id;
            const params = paramItemsMapByEstudio[idE] || [];
            const hijos = node.hijos || [];
            const esSub = isSubEstudioFlag(est);
            const tieneParamsMain = params.length > 0;
            const tieneHijos = hijos.length > 0;

            if (esSub) {
              if (tieneParamsMain) {
                if (params.some(p => tieneValorParametro(p))) {
                  tablas.push({
                    nombreTitulo: (est.nombre || "").toUpperCase(),
                    estudioPrincipalTabla: est,
                    rows: params.map(p => ({ type: "parameter", item: p })),
                  });
                }
                for (const child of hijos) {
                  tablas.push(...generarTablasPDF(child));
                }
              } else {
                if (tieneHijos) {
                  let headerSuperiorAsignado = false;
                  for (let i = 0; i < hijos.length; i++) {
                    const tablasHijo = generarTablasPDF(hijos[i]);
                    if (tablasHijo.length > 0) {
                      if (!headerSuperiorAsignado) {
                        tablasHijo[0].nombreHeaderSuperior = (est.nombre || "").toUpperCase();
                        headerSuperiorAsignado = true;
                      }
                      tablas.push(...tablasHijo);
                    }
                  }
                }
              }
            } else {
              const rowsBox = [];
              if (tieneParamsMain) {
                params.forEach(p => rowsBox.push({ type: "parameter", item: p }));
              }

              const agregarSubEstudiosMismaTabla = (n) => {
                for (const child of n.hijos || []) {
                  const childEst = child.estudio || {};
                  const esSubFlag = isSubEstudioFlag(childEst);
                  if (esSubFlag) continue;

                  if (!subArbolTieneValoresMismaTabla(child)) {
                    continue;
                  }

                  const childIdE = childEst.idEstudio || childEst.id;
                  const childParams = paramItemsMapByEstudio[childIdE] || [];
                  const esMismoNombreUnicoParam =
                    !esSubFlag &&
                    childParams.length === 1 &&
                    normStr(childEst.nombre) === normStr(childParams[0]?.parametro?.parametro || childParams[0]?.parametro?.nombre);

                  if (!esMismoNombreUnicoParam) {
                    rowsBox.push({
                      type: "subHeader",
                      nombre: (childEst.nombre || "").toUpperCase(),
                    });
                  }

                  childParams.forEach(p => rowsBox.push({ type: "parameter", item: p }));

                  if ((child.hijos || []).length > 0) {
                    if (!isSubEstudioFlag(childEst)) {
                      agregarSubEstudiosMismaTabla(child);
                    }
                  }
                }
              };

              agregarSubEstudiosMismaTabla(node);

              if (rowsBox.some(r => r.type === "parameter" && tieneValorParametro(r.item))) {
                tablas.push({
                  nombreTitulo: (est.nombre || "").toUpperCase(),
                  estudioPrincipalTabla: est,
                  rows: rowsBox,
                });
              }

              const agregarSubTablasAparte = (n) => {
                for (const child of n.hijos || []) {
                  if (isSubEstudioFlag(child.estudio)) {
                    tablas.push(...generarTablasPDF(child));
                  } else if ((child.hijos || []).length > 0) {
                    agregarSubTablasAparte(child);
                  }
                }
              };
              agregarSubTablasAparte(node);
            }

            return tablas;
          };

          const detMain = await EstudiosService.getEstudioPorId(mainIdEst) || estudio;
          const arbolMain = await cargarNodoRecursivo(detMain, null);
          const tablasPDFList = generarTablasPDF(arbolMain);

          const tablasValidas = tablasPDFList.filter(t => {
            return (t.rows || []).some(
              r => r.type === "parameter" && tieneValorParametro(r.item)
            );
          });

          if (tablasValidas.length === 0) {
            Swal.fire({
              icon: "info",
              title: "Sin resultados para imprimir",
              text: "El estudio no cuenta con ningún resultado capturado.",
              confirmButtonColor: "#006787"
            });
            return;
          }

          // Evaluación del primer parámetro global
          let primerParam = null;
          for (const t of tablasValidas) {
            const pItem = (t.rows || []).find(r => r.type === "parameter");
            if (pItem) {
              primerParam = pItem.item?.parametro;
              break;
            }
          }
          const primerParametroEsBR = primerParam ? esParametroBR(primerParam) : false;

          const tableHeadRow2 = primerParametroEsBR
            ? [
              { content: "", styles: { fillColor: [175, 198, 215] } },
              { content: "Bajo", styles: { fillColor: [175, 198, 215], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 7.5, halign: "center", cellPadding: 1 } },
              { content: "Dentro", styles: { fillColor: [175, 198, 215], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 7.5, halign: "center", cellPadding: 1 } },
              { content: "Sobre", styles: { fillColor: [175, 198, 215], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 7.5, halign: "center", cellPadding: 1 } },
              { content: "UM", styles: { fillColor: [175, 198, 215], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 7.5, halign: "center", cellPadding: 1 } },
              { content: "Intervalo Biológico de Referencia", styles: { fillColor: [175, 198, 215], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 7.5, halign: "center", cellPadding: 1 } }
            ]
            : [
              { content: "", styles: { fillColor: [175, 198, 215] } },
              { content: "Resultado", colSpan: 3, styles: { fillColor: [175, 198, 215], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 7.5, halign: "center", cellPadding: 1 } },
              { content: "UM", styles: { fillColor: [175, 198, 215], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 7.5, halign: "center", cellPadding: 1 } },
              { content: "Intervalo Biológico de Referencia", styles: { fillColor: [175, 198, 215], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 7.5, halign: "center", cellPadding: 1 } }
            ];

          let currentY = yMuestra + 1.8;

          for (let tIdx = 0; tIdx < tablasValidas.length; tIdx++) {
            const tablaData = tablasValidas[tIdx];

            const headRows = [];
            if (tablaData.nombreHeaderSuperior) {
              headRows.push([
                {
                  content: tablaData.nombreHeaderSuperior,
                  colSpan: 6,
                  styles: {
                    fillColor: [175, 198, 215],
                    textColor: [0, 0, 0],
                    fontStyle: "bolditalic",
                    fontSize: 9.5,
                    halign: "left",
                    cellPadding: 1.5
                  }
                }
              ]);
            }

            headRows.push([
              {
                content: (tablaData.nombreTitulo || "").toUpperCase(),
                colSpan: 6,
                styles: {
                  fillColor: [175, 198, 215],
                  textColor: [0, 0, 0],
                  fontStyle: "bolditalic",
                  fontSize: 9.5,
                  halign: "left",
                  cellPadding: 1.5
                }
              }
            ]);
            headRows.push(tableHeadRow2);

            const tableBodyRows = [];

            (tablaData.rows || []).forEach(rObj => {
              if (rObj.type === "subHeader") {
                tableBodyRows.push([
                  {
                    content: String(rObj.nombre || "").toUpperCase(),
                    colSpan: 6,
                    isSubHeader: true,
                    styles: {
                      fillColor: [255, 255, 255],
                      textColor: [0, 0, 0],
                      fontStyle: "bolditalic",
                      fontSize: 9,
                      halign: "left",
                      cellPadding: 1.5
                    }
                  }
                ]);
              } else if (rObj.type === "parameter") {
                const item = rObj.item || {};
                const p = item.parametro || {};
                const paramName = (p.parametro || p.nombre || "").toUpperCase();
                const resultVal = item.resultValue || "";
                const cleanResult = htmlToPlainText(resultVal);
                const unidad = p.unidadMedida || p.unidad || "";
                const pacCompat = {
                  sexo: solicitudInfo.sexo,
                  edad: solicitudInfo.edad
                };
                const refStr = formatParametroIntervalos(p, pacCompat);

                let bajoCell = "";
                let dentroCell = "";
                let sobreCell = "";

                const numVal = parseFloat(cleanResult);
                const isVerified = item.verificado === true || item.verificado === 1 || String(item.verificado) === "true";
                const valDisplay = isVerified ? `${cleanResult}\n(Verificado)` : cleanResult;

                const esBR = esParametroBR(p);

                if (esBR && primerParametroEsBR) {
                  if (!isNaN(numVal) && refStr) {
                    let min = null;
                    let max = null;

                    const rangeMatch = String(refStr).match(/(\d+(?:\.\d+)?)\s*[-–—aA]\s*(\d+(?:\.\d+)?)/);
                    if (rangeMatch) {
                      min = parseFloat(rangeMatch[1]);
                      max = parseFloat(rangeMatch[2]);
                    } else {
                      const maxMatch = String(refStr).match(/<=\s*(\d+(?:\.\d+)?)|<\s*(\d+(?:\.\d+)?)/);
                      if (maxMatch) {
                        min = -Infinity;
                        max = parseFloat(maxMatch[1] || maxMatch[2]);
                      } else {
                        const minMatch = String(refStr).match(/>=\s*(\d+(?:\.\d+)?)|>\s*(\d+(?:\.\d+)?)/);
                        if (minMatch) {
                          min = parseFloat(minMatch[1] || minMatch[2]);
                          max = Infinity;
                        }
                      }
                    }

                    if (min !== null && max !== null) {
                      if (numVal < min) {
                        bajoCell = valDisplay;
                      } else if (numVal > max) {
                        sobreCell = valDisplay;
                      } else {
                        dentroCell = valDisplay;
                      }
                    } else {
                      dentroCell = valDisplay;
                    }
                  } else {
                    dentroCell = valDisplay;
                  }

                  tableBodyRows.push([
                    paramName,
                    bajoCell,
                    dentroCell,
                    sobreCell,
                    unidad,
                    refStr
                  ]);
                } else {
                  tableBodyRows.push([
                    paramName,
                    { content: valDisplay, colSpan: 3, styles: { halign: "center" } },
                    unidad,
                    refStr
                  ]);
                }

                if (p.textoExtra && htmlToPlainText(p.textoExtra).trim() !== "") {
                  tableBodyRows.push([
                    {
                      content: htmlToPlainText(p.textoExtra),
                      colSpan: 6,
                      styles: { halign: "left", fontStyle: "normal", fontSize: 8, cellPadding: 1.5 }
                    }
                  ]);
                }
              }
            });

            if (currentY > 250) {
              doc.addPage();
              currentY = 20;
            }

            autoTable(doc, {
              startY: currentY,
              head: headRows,
              body: tableBodyRows,
              theme: "grid",
              headStyles: {
                lineWidth: 0.3,
                lineColor: [150, 170, 190]
              },
              bodyStyles: {
                fontSize: 8.5,
                textColor: [30, 30, 30],
                valign: "middle",
                lineWidth: 0.15,
                lineColor: [220, 220, 220]
              },
              columnStyles: {
                0: { cellWidth: 64, halign: "left" },
                1: { cellWidth: 17, halign: "center" },
                2: { cellWidth: 17, halign: "center" },
                3: { cellWidth: 17, halign: "center" },
                4: { cellWidth: 23, halign: "center" },
                5: { cellWidth: 52, halign: "center", fontStyle: "bold" }
              },
              didParseCell: function (data) {
                if (data.section === "body" && data.column.index === 5) {
                  data.cell.styles.fontStyle = "bold";
                }
                if (data.section === "body" && [1, 2, 3].includes(data.column.index)) {
                  const rawVal = data.cell.raw?.content !== undefined ? data.cell.raw.content : data.cell.raw;
                  const cellText = Array.isArray(data.cell.text) ? data.cell.text.join("\n") : String(rawVal || "");
                  if (cellText.includes("(Verificado)")) {
                    data.cell.styles.fontStyle = "bold";
                    data.cell.styles.textColor = [0, 0, 0];
                    data.cell.styles.fontSize = 7.2;
                  }
                }
              },
              margin: { left: 10, right: 10 }
            });

            let finalY = doc.lastAutoTable.finalY + 4;
            const estPrincipalTabla = tablaData.estudioPrincipalTabla || {};
            const idEstTabla = estPrincipalTabla.idEstudio || estPrincipalTabla.id;

            // Renderizar Observación / NOTA del estudio debajo de la tabla
            // Si no son subexamenes (vienen juntos en una sola tabla), solo se toma la observación del padre.
            // Si sí son subexamenes (tablas separadas), se toma la observación de ese subexamen.
            const idsEstaTabla = new Set([String(idEstTabla)]);
            (tablaData.rows || []).forEach(r => {
              const rId = r.item?.idEstudio || r.idEstudio;
              if (rId) idsEstaTabla.add(String(rId));
            });

            let obsRaw = "";
            const currentObsMem = (observacionesRef.current !== undefined && observacionesRef.current !== "")
              ? observacionesRef.current
              : observaciones;

            if (idsEstaTabla.has(String(estudio?.idEstudio || mainIdEst)) && currentObsMem && String(currentObsMem).trim()) {
              obsRaw = currentObsMem;
            } else if (Array.isArray(rawResultados)) {
              const resPadre = rawResultados.find(
                r => String(r.idEstudio) === String(idEstTabla) && r.observaciones && String(r.observaciones).trim() !== ""
              );
              if (resPadre) {
                obsRaw = resPadre.observaciones;
              } else {
                const resConObs = rawResultados.find(
                  r => idsEstaTabla.has(String(r.idEstudio)) && r.observaciones && String(r.observaciones).trim() !== ""
                );
                if (resConObs) {
                  obsRaw = resConObs.observaciones;
                }
              }
            }
            if (!obsRaw && (tablaData.rows || []).length > 0) {
              for (const rItem of tablaData.rows) {
                const rowEstId = String(rItem.item?.idEstudio || idEstTabla);
                if (!idsEstaTabla.has(rowEstId)) continue;
                const pId = rItem.item?.parametro?.idParametrosEstudio || rItem.item?.parametro?.id;
                const rObj = resultMap[`${rowEstId}-${pId}`] || resultMap[`${idEstTabla}-${pId}`];
                if (rObj && rObj.observaciones && String(rObj.observaciones).trim()) {
                  obsRaw = rObj.observaciones;
                  break;
                }
              }
            }

            const obsTexto = htmlToPlainText(obsRaw).trim();

            let metVal = String(estPrincipalTabla.metodologia || estPrincipalTabla.metodo || estPrincipalTabla.tecnica || "").trim();
            let muestraVal = String(estPrincipalTabla.tipoMuestra || estPrincipalTabla.muestra || estPrincipalTabla.material || "").trim();

            if (!metVal && idEstTabla) {
              try {
                const det = await EstudiosService.getEstudioPorId(idEstTabla);
                metVal = String(det?.metodologia || det?.metodo || det?.tecnica || "").trim();
              } catch (e) { }
            }

            if (!muestraVal && idEstTabla) {
              try {
                const resM = await MuestrasService.getMuestrasPorEstudio(idEstTabla);
                if (Array.isArray(resM) && resM.length > 0) {
                  muestraVal = String(resM[0].tipoMuestra || resM[0].nombreMaterial || resM[0].material || "").trim();
                }
              } catch (e) { }
            }

            let regLiberadoTabla = null;
            if (idSol) {
              const rawIds = [
                idEstTabla,
                estPrincipalTabla?.idEstudioPadre,
                estPrincipalTabla?.idPadre,
                estPrincipalTabla?.idEstudioSec,
                mainIdEst,
              ].filter(Boolean);

              const idsToTry = [...new Set(rawIds)];

              for (const idCheck of idsToTry) {
                try {
                  const histEst = await HistorialEstudiosService.getHistorialPorEstudio(idSol, idCheck);
                  if (Array.isArray(histEst)) {
                    const found = histEst.find(h => String(h.tipoAccion).toUpperCase() === "L" || String(h.tipoAccion).toUpperCase() === "LIBERADO");
                    if (found) {
                      regLiberadoTabla = found;
                      break;
                    }
                  }
                } catch (e) { }
              }

              if (!regLiberadoTabla) {
                try {
                  const localHist = HistorialEstudiosService._getLocalHistorial();
                  const regLocal = localHist.find(
                    h => String(h.idSolicitud) === String(idSol) &&
                      idsToTry.map(String).includes(String(h.idEstudio)) &&
                      (String(h.tipoAccion).toUpperCase() === "L" || String(h.tipoAccion).toUpperCase() === "LIBERADO")
                  );
                  if (regLocal) {
                    regLiberadoTabla = regLocal;
                  }
                } catch (eLoc) { }
              }
            }

            let nomLiberadorTabla = regLiberadoTabla?.nombreEmpleado || currentU?.nombre || "EMPLEADO LIBERADOR";
            let cedTabla = currentU?.cedulaProf || currentU?.cedula || "";
            let fechaLibTabla = fechaImpresionStr;

            if (regLiberadoTabla && (regLiberadoTabla.fechaModificacion || regLiberadoTabla.fecha)) {
              const rawF = regLiberadoTabla.fechaModificacion || regLiberadoTabla.fecha;
              const dF = new Date(rawF);
              if (!isNaN(dF.getTime())) {
                const diaF = String(dF.getDate()).padStart(2, '0');
                const mesF = meses[dF.getMonth()];
                const anioF = dF.getFullYear();
                const hrsF = String(dF.getHours()).padStart(2, '0');
                const minsF = String(dF.getMinutes()).padStart(2, '0');
                fechaLibTabla = `${diaF}/${mesF}/${anioF} ${hrsF}:${minsF} h`;
              } else {
                fechaLibTabla = String(rawF);
              }
            }

            if (finalY > 255) {
              doc.addPage();
              finalY = 20;
            }

            doc.setFontSize(7.5);
            doc.setTextColor(0, 0, 0);
            const rightX = 200;

            const metMuestraStr = `Metodología: ${metVal}   Tipo de muestra: ${muestraVal}`;
            doc.setFont("helvetica", "bold");
            doc.text(metMuestraStr, rightX, finalY, { align: "right" });

            finalY += 3.8;
            const liberoStr = `Liberó: ${nomLiberadorTabla}${cedTabla ? `   Ced. Prof.: ${cedTabla}` : ""}`;
            doc.setFont("helvetica", "bold");
            doc.text(liberoStr, rightX, finalY, { align: "right" });

            finalY += 3.8;
            const fechaLibStr = `Fecha y hora de liberación: ${fechaLibTabla}`;
            doc.setFont("helvetica", "bold");
            doc.text(fechaLibStr, rightX, finalY, { align: "right" });

            // Renderizar Observación / NOTA del estudio al final con texto enriquecido (debajo de Metodología, Tipo de Muestra y Liberación)
            const obsBlocks = parseHtmlToStyledBlocks(obsRaw);
            if (obsBlocks.length > 0) {
              finalY += 4.5;
              finalY = renderRichTextInPdf(
                doc,
                obsBlocks,
                10,
                finalY,
                190,
                255,
                14,
                9.0,
                4.5
              );
            }

            currentY = finalY + 7;
          }

          // Marca de agua "REPORTE SIN VALIDEZ" si estatus no es Liberado ("L")

          const totalPages = doc.internal.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.saveGraphicsState();
            doc.setGState(new doc.GState({ opacity: 0.12 }));
            doc.setFont("helvetica", "bold");
            doc.setFontSize(36);
            doc.setTextColor(180, 0, 0);
            doc.text("REPORTE SIN VALIDEZ", 105, 150, { align: "center", angle: 45 });
            doc.restoreGraphicsState();
          }


          // Visor PDF en SweetAlert2
          const pdfBlob = doc.output("bloburl");
          Swal.fire({
            title: "Informe de Resultados",
            html: `
          <div style="width: 100%; height: 75vh;">
            <iframe src="${pdfBlob}#toolbar=1" width="100%" height="100%" style="border: none; border-radius: 8px;"></iframe>
          </div>
        `,
            width: "82vw",
            showConfirmButton: false,
            showCloseButton: true
          });
        },
        "Generando informe de resultados...",
        "Procesando PDF"
      );
    } catch (errPDF) {
      console.error("Error al generar el informe de resultados PDF:", errPDF);
      mostrarError("Ocurrió un error al generar el PDF del informe de resultados.");
    }
  };

  return (
    <Layout>
      <Content>
        {cargando ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
            <CircularProgress size="4vw" />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              height: "50%",
              paddingTop: "2vh",
              paddingBottom: "2vh",
            }}
          >
            <Box sx={{ width: "95vw", maxWidth: "96vw", mb: 2 }}>
              <Stack spacing={2} sx={{ width: "100%" }}>
                <Stack direction="row" spacing={"1vw"} sx={{ alignItems: "stretch", width: "85vw" }}>
                  <Paper
                    elevation={3}
                    sx={{ p: 1, borderRadius: 4, width: "65%" }}
                    className="paperSolicitudResultados"
                  >
                    <Grid item xs={12} container spacing={"1vw"} sx={{ paddingTop: "0vw" }}>
                      <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                        <Typography
                          fontWeight="bold"
                          align="center"
                          sx={{
                            background: "linear-gradient(90deg, #006787, #014e65)",
                            color: "white",
                            padding: "4px 12px",
                            borderRadius: "6px",
                            display: "inline-block",
                            marginTop: "0.2vw",
                            fontSize: "0.85rem",
                          }}
                        >
                          SOLICITUD: #{solicitudInfo.codigo}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} container spacing={"1vw"} sx={{ paddingTop: "0.4vw", justifyContent: "center" }}>
                      <Box sx={{ width: "100%", overflowX: "auto" }}>
                        <Table sx={{ width: "100%", minWidth: 0, tableLayout: "fixed", backgroundColor: "white" }}>
                          <TableBody sx={{ "& .MuiTableCell-root": { padding: "4px 8px" } }}>
                            <TableRow>
                              <TableCell sx={{ backgroundColor: "#18234f", width: "15%", color: "white", fontWeight: "bold", fontSize: "0.75rem" }}>
                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                  Paciente:
                                </Box>
                              </TableCell>
                              <TableCell colSpan={5} sx={{ fontWeight: "bold", color: "black", fontSize: "0.75rem" }}>
                                {solicitudInfo.nombrePaciente}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ backgroundColor: "#18234f", color: "white", fontWeight: "bold", fontSize: "0.75rem" }}>
                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                  Sexo:
                                </Box>
                              </TableCell>
                              <TableCell sx={{ color: "black", fontSize: "0.75rem" }}>
                                {solicitudInfo.sexo}
                              </TableCell>
                              <TableCell sx={{ backgroundColor: "#18234f", color: "white", fontWeight: "bold", fontSize: "0.75rem" }}>
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                  Fecha de Nacimiento:
                                </Box>
                              </TableCell>
                              <TableCell sx={{ color: "black", fontSize: "0.75rem" }}>
                                {solicitudInfo.fechaNacimiento}
                              </TableCell>
                              <TableCell sx={{ backgroundColor: "#18234f", color: "white", fontWeight: "bold", fontSize: "0.75rem" }}>
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                  Edad:
                                </Box>
                              </TableCell>
                              <TableCell sx={{ color: "black", fontSize: "0.75rem" }}>
                                {solicitudInfo.edad}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ backgroundColor: "#18234f", color: "white", fontWeight: "bold", fontSize: "0.75rem" }}>
                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                  Doctor:
                                </Box>
                              </TableCell>
                              <TableCell colSpan={5} sx={{ color: "black", fontWeight: "bold", fontSize: "0.75rem" }}>
                                {solicitudInfo.nombreDoctor}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ backgroundColor: "#18234f", color: "white", fontWeight: "bold", fontSize: "0.75rem" }}>
                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                  Empresa:
                                </Box>
                              </TableCell>
                              <TableCell colSpan={5} sx={{ color: "black", fontWeight: "bold", fontSize: "0.75rem" }}>
                                {solicitudInfo.nombreConvenio}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Box>
                    </Grid>
                  </Paper>

                  <Paper
                    elevation={3}
                    sx={{ p: 1, borderRadius: 4, width: "35%" }}
                    className="paperSolicitudResultados"
                  >
                    <Grid container spacing={1} direction="column" justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
                      <Grid item>
                        <Typography
                          fontWeight="bold"
                          align="center"
                          sx={{
                            background: "linear-gradient(90deg, #006787, #014e65)",
                            color: "white",
                            padding: "4px 12px",
                            borderRadius: "6px",
                            display: "inline-block",
                            fontSize: "0.85rem",
                          }}
                        >
                          ESTATUS DEL ESTUDIO
                        </Typography>
                      </Grid>

                      <Grid item sx={{ width: "100%", mt: 1 }}>
                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ width: "100%", flexWrap: "wrap", gap: "6px" }}>
                          <Button
                            variant={estatusEstudio === "P" ? "contained" : "outlined"}
                            disabled={isReadOnlyUI}
                            onClick={() => { setEstatusEstudio("P"); autoGuardar("P"); }}
                            sx={{
                              flex: 1,
                              minWidth: "75px",
                              backgroundColor: estatusEstudio === "P" ? "#5b6264" : "white",
                              color: estatusEstudio === "P" ? "white" : "#5b6264",
                              borderColor: "#5b6264",
                              borderRadius: "6px",
                              padding: "4px 8px",
                              fontSize: "0.72rem",
                              fontWeight: "bold",
                              textTransform: "none",
                              "&:hover": {
                                backgroundColor: estatusEstudio === "P" ? "#4f5557" : "rgba(91,98,100,0.1)",
                                borderColor: "#5b6264",
                              }
                            }}
                          >
                            En proceso
                          </Button>
                          <Button
                            variant={estatusEstudio === "R" ? "contained" : "outlined"}
                            disabled={isReadOnlyUI}
                            onClick={() => { setEstatusEstudio("R"); autoGuardar("R"); }}
                            sx={{
                              flex: 1,
                              minWidth: "75px",
                              backgroundColor: estatusEstudio === "R" ? "#62abc0" : "white",
                              color: estatusEstudio === "R" ? "white" : "#62abc0",
                              borderColor: "#62abc0",
                              borderRadius: "6px",
                              padding: "4px 8px",
                              fontSize: "0.72rem",
                              fontWeight: "bold",
                              textTransform: "none",
                              "&:hover": {
                                backgroundColor: estatusEstudio === "R" ? "#559eaf" : "rgba(98,171,192,0.1)",
                                borderColor: "#62abc0",
                              }
                            }}
                          >
                            Reportado
                          </Button>
                          <Button
                            variant={estatusEstudio === "V" ? "contained" : "outlined"}
                            disabled={isReadOnlyUI}
                            onClick={() => { setEstatusEstudio("V"); autoGuardar("V"); }}
                            sx={{
                              flex: 1,
                              minWidth: "75px",
                              backgroundColor: estatusEstudio === "V" ? "#e4aa53" : "white",
                              color: estatusEstudio === "V" ? "white" : "#e4aa53",
                              borderColor: "#e4aa53",
                              borderRadius: "6px",
                              padding: "4px 8px",
                              fontSize: "0.72rem",
                              fontWeight: "bold",
                              textTransform: "none",
                              "&:hover": {
                                backgroundColor: estatusEstudio === "V" ? "#d19945" : "rgba(228,170,83,0.1)",
                                borderColor: "#e4aa53",
                              }
                            }}
                          >
                            Revisado
                          </Button>
                          <Button
                            variant={estatusEstudio === "L" ? "contained" : "outlined"}
                            disabled={isReadOnlyUI || (estatusEstudio !== "V" && estatusEstudio !== "L")}
                            onClick={handleLiberarClick}
                            sx={{
                              flex: 1,
                              minWidth: "75px",
                              backgroundColor: estatusEstudio === "L" ? "#57ad57" : "white",
                              color: estatusEstudio === "L" ? "white" : "#57ad57",
                              borderColor: "#57ad57",
                              borderRadius: "6px",
                              padding: "4px 8px",
                              fontSize: "0.72rem",
                              fontWeight: "bold",
                              textTransform: "none",
                              "&:hover": {
                                backgroundColor: estatusEstudio === "L" ? "#4da34d" : "rgba(87,173,87,0.1)",
                                borderColor: "#57ad57",
                              }
                            }}
                          >
                            Liberado
                          </Button>
                        </Stack>
                        <Typography sx={{ color: "red", fontWeight: "bold", fontSize: "0.7rem", marginTop: "12px", textAlign: "center" }}>
                          NOTA: Los campos que no sean llenados, no aparecerán en el reporte.
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Stack>
              </Stack>
              <Paper elevation={3} sx={{ p: 2, borderRadius: 4, marginTop: "2vh", width: "85vw" }} className="paperDoc">
                <Table sx={{ backgroundColor: "white" }} size="small">
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell sx={{ color: "white", fontWeight: "bold", width: "24%", fontSize: "0.82rem", py: 1 }}>Estudio</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold", width: "14%", fontSize: "0.82rem", py: 1 }}>Opciones</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold", width: "32%", fontSize: "0.82rem", py: 1 }}>Resultados del Estudio</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold", width: "10%", fontSize: "0.82rem", py: 1 }}>UC</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold", width: "15%", fontSize: "0.82rem", py: 1 }}>Valores de referencia</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold", width: "5%", fontSize: "0.82rem", py: 1 }}>
                        <Tooltip title="Marcar / Desmarcar todos como verificados">
                          <IconButton
                            onClick={handleToggleAllVerificado}
                            disabled={isReadOnlyUI}
                            size="small"
                            tabIndex={-1}
                            sx={{ color: "white", p: 0.2 }}
                          >
                            <CheckCircleIcon sx={{ fontSize: "1.3rem" }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {estudiosYParametros.length > 0 ? (
                      estudiosYParametros.map((item, index) => {
                        if (item.type === "studyHeader") {
                          return (
                            <TableRow key={`header-${index}`} sx={{ backgroundColor: item.isSubEstudio ? "#e3f2fd" : "#cfd8dc" }}>
                              <TableCell colSpan={6} sx={{ py: 0.8, px: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: "uppercase", fontStyle: item.isSubEstudio ? "italic" : "normal", color: item.isSubEstudio ? "#0d47a1" : "#263238", fontSize: item.isSubEstudio ? "0.8rem" : "0.9rem", fontFamily: "Roboto-Medium", pl: item.nivel ? (item.nivel * 3) : 0 }}>
                                  {item.nombre}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        }

                        const p = item.parametro;
                        const refType = String(p.tipoReferencia || "").toLowerCase();
                        const isQuill = refType === "mixto" || refType.includes("criterio");

                        return (
                          <TableRow key={`param-${index}`} hover>
                            <TableCell sx={{ fontWeight: "bold", color: "#333", fontSize: "0.8rem", pl: item.nivel ? (4 + item.nivel * 3) : 4, py: 0.6 }}>
                              {p.parametro}
                            </TableCell>
                            <TableCell sx={{ py: 0.6 }}>
                              {refType === "mixto" && (p.valoresParametro || []).length > 0 ? (
                                <FormControl size="small" fullWidth disabled={isReadOnlyUI}>
                                  <InputLabel id={`select-label-${index}`} sx={{ fontSize: "0.72rem" }}>Seleccione</InputLabel>
                                  <Select
                                    labelId={`select-label-${index}`}
                                    label="Seleccione"
                                    value=""
                                    disabled={isReadOnlyUI}
                                    onChange={(e) => handleSelectOptionMixto(index, e.target.value)}
                                    sx={{ height: "30px", fontSize: "0.75rem", backgroundColor: "white" }}
                                  >
                                    {(p.valoresParametro || []).map((valObj, vidx) => (
                                      <MenuItem key={vidx} value={valObj.valor} sx={{ fontSize: "0.75rem" }}>
                                        {valObj.valor} {valObj.esPredeterminado ? " (Predet.)" : ""}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : null}
                            </TableCell>
                            <TableCell sx={{ py: 0.6 }}>
                              {isQuill ? (
                                <Box sx={{
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  backgroundColor: "white",
                                  "& .ql-toolbar": {
                                    py: 0.3,
                                    px: 0.5,
                                    borderColor: "#ccc",
                                  },
                                  "& .ql-container": {
                                    border: "none",
                                    fontSize: "0.8rem",
                                    fontFamily: "inherit",
                                  },
                                  "& .ql-editor": {
                                    minHeight: "50px",
                                    maxHeight: "100px",
                                    overflowY: "auto",
                                    fontSize: "0.8rem",
                                    py: 0.5,
                                    px: 1,
                                  },
                                }}>
                                  <ReactQuill
                                    theme="snow"
                                    readOnly={isReadOnlyUI}
                                    value={item.resultValue || ""}
                                    onChange={(content) => handleValueChange(index, content)}
                                    onBlur={() => autoGuardar()}
                                    modules={{
                                      toolbar: isReadOnlyUI ? false : [
                                        ['bold', 'italic', 'underline'],
                                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                        ['clean']
                                      ],
                                    }}
                                  />
                                </Box>
                              ) : (
                                <TextField
                                  fullWidth
                                  size="small"
                                  disabled={isReadOnlyUI || item.isCalculado}
                                  placeholder={item.isCalculado ? "Calculado automáticamente" : ""}
                                  value={item.resultValue || ""}
                                  onChange={(e) => handleValueChange(index, e.target.value)}
                                  onBlur={() => autoGuardar()}
                                  sx={{
                                      "& .MuiOutlinedInput-root": {
                                        height: "30px",
                                        backgroundColor: item.isCalculado
                                          ? "#f0f0f0"
                                          : item.validationStatus === "ALTO"
                                          ? "#ffebee"
                                          : item.validationStatus === "BAJO"
                                          ? "#e3f2fd"
                                          : "white",
                                        "& fieldset": {
                                          borderColor: item.isCalculado
                                            ? "#d6d6d6"
                                            : item.validationStatus === "ALTO"
                                            ? "#ef9a9a"
                                            : item.validationStatus === "BAJO"
                                            ? "#90caf9"
                                            : "rgba(0, 0, 0, 0.23)",
                                        },
                                        "&:hover fieldset": {
                                          borderColor: item.isCalculado
                                            ? "#d6d6d6"
                                            : item.validationStatus === "ALTO"
                                            ? "#e57373"
                                            : item.validationStatus === "BAJO"
                                            ? "#64b5f6"
                                            : "rgba(0, 0, 0, 0.87)",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor: item.isCalculado
                                            ? "#d6d6d6"
                                            : item.validationStatus === "ALTO"
                                            ? "#c62828"
                                            : item.validationStatus === "BAJO"
                                            ? "#1565c0"
                                            : "#1976d2",
                                        },
                                        "&.Mui-disabled": {
                                          backgroundColor: item.isCalculado
                                            ? "#f0f0f0"
                                            : item.validationStatus === "ALTO"
                                            ? "#ffebee"
                                            : item.validationStatus === "BAJO"
                                            ? "#e3f2fd"
                                            : "#fafafa",
                                          "& fieldset": {
                                            borderColor: item.isCalculado
                                              ? "#d6d6d6"
                                              : item.validationStatus === "ALTO"
                                              ? "#ef9a9a"
                                              : item.validationStatus === "BAJO"
                                              ? "#90caf9"
                                              : "rgba(0, 0, 0, 0.12)",
                                          }
                                        }
                                      },
                                      "& .MuiInputBase-input": {
                                        fontSize: "0.8rem",
                                        color: item.isCalculado
                                          ? "#757575"
                                          : item.validationStatus === "ALTO"
                                          ? "#c62828"
                                          : item.validationStatus === "BAJO"
                                          ? "#1565c0"
                                          : "black",
                                        fontWeight: (!item.isCalculado && (item.validationStatus === "ALTO" || item.validationStatus === "BAJO"))
                                          ? "bold"
                                          : "normal",
                                        "&.Mui-disabled": {
                                          color: item.isCalculado
                                            ? "#757575"
                                            : item.validationStatus === "ALTO"
                                            ? "#c62828"
                                            : item.validationStatus === "BAJO"
                                            ? "#1565c0"
                                            : "rgba(0, 0, 0, 0.38)",
                                          WebkitTextFillColor: item.isCalculado
                                            ? "#757575"
                                            : item.validationStatus === "ALTO"
                                            ? "#c62828"
                                            : item.validationStatus === "BAJO"
                                            ? "#1565c0"
                                            : "rgba(0, 0, 0, 0.38)",
                                        }
                                      }
                                    }}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8rem", py: 0.6 }}>
                              {p.unidadMedida || p.ui || ""}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8rem", color: "#666", py: 0.6 }}>
                              {isQuill ? (
                                <span dangerouslySetInnerHTML={{ __html: p.valorReferencia || "" }} />
                              ) : item.matchingInterval ? (
                                `${item.matchingInterval.intervaloMin} a ${item.matchingInterval.intervaloMax} ${p.unidadMedida || p.ui || ""}`
                              ) : (
                                <span dangerouslySetInnerHTML={{ __html: p.valorReferencia || "Sin referencia" }} />
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.6 }}>
                              <Checkbox
                                checked={!!item.verificado}
                                disabled={isReadOnlyUI}
                                onChange={(e) => handleVerificadoChange(index, e.target.checked)}
                                size="small"
                                tabIndex={-1}
                                inputProps={{ tabIndex: -1 }}
                                sx={{
                                  color: "#18234f",
                                  "&.Mui-checked": {
                                    color: "#2e7d32",
                                  },
                                  p: 0.2
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          No hay registros
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
              <Paper elevation={3} sx={{ p: 2, borderRadius: 4, marginTop: "2vh", width: "85vw" }} className="paperDoc">
                <Box sx={{ width: "100%", backgroundColor: "#cfd8dc", py: 1, px: 2, borderRadius: "4px 4px 0 0" }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: "uppercase", color: "#263238", fontSize: "0.9rem", fontFamily: "Roboto-Medium" }}>
                    Coloque las observaciones del estudio {estudio?.nombre || estudio?.nombreEstudio || ""}:
                  </Typography>
                </Box>
                <Box sx={{
                  width: "100%",
                  border: "1px solid #ccc",
                  borderTop: "none",
                  borderRadius: "0 0 4px 4px",
                  backgroundColor: "white",
                  "& .ql-container": {
                    border: "none",
                    fontSize: "0.85rem",
                    fontFamily: "inherit",
                  },
                  "& .ql-editor": {
                    minHeight: "100px",
                    maxHeight: "220px",
                    overflowY: "auto",
                    fontSize: "0.85rem",
                  },
                }}>
                  <ReactQuill
                    theme="snow"
                    readOnly={isReadOnlyUI}
                    value={observaciones || ""}
                    onChange={(content) => {
                      setObservaciones(content);
                      observacionesRef.current = content;
                      if (debounceObsTimerRef.current) clearTimeout(debounceObsTimerRef.current);
                      debounceObsTimerRef.current = setTimeout(() => {
                        autoGuardar(estatusEstudio, content);
                      }, 1200);
                    }}
                    onBlur={() => {
                      if (debounceObsTimerRef.current) clearTimeout(debounceObsTimerRef.current);
                      const obsActual = (observacionesRef.current !== undefined && observacionesRef.current !== null)
                        ? observacionesRef.current
                        : observaciones;
                      autoGuardar(estatusEstudio, obsActual);
                    }}
                    modules={{
                      toolbar: isReadOnlyUI ? false : [
                        ['bold', 'italic', 'underline'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        ['clean']
                      ],
                    }}
                  />
                </Box>
              </Paper>
            </Box>
          </Box>
        )}
        {(estatusEstudio === "V" || estatusEstudio === "L") && (
          <Box
            sx={{
              position: "fixed",
              right: "3vw",
              top: "35%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              zIndex: 1000
            }}
          >
            <Tooltip title="Ver Informe de Resultados PDF" arrow placement="left">
              <IconButton
                onClick={handleGenerarInformeResultadosPDF}
                sx={{
                  backgroundColor: "#006787",
                  color: "white",
                  width: "3.5vw",
                  height: "3.5vw",
                  boxShadow: "0px 4px 10px rgba(0,0,0,0.3)",
                  "&:hover": {
                    backgroundColor: "#004d66"
                  }
                }}
              >
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <VisibilityIcon sx={{ fontSize: "1.8vw", color: "white" }} />
                  <AssignmentIcon
                    sx={{
                      fontSize: "1vw",
                      color: "#006787",
                      position: "absolute",
                      bottom: -2,
                      right: -2,
                      backgroundColor: "white",
                      borderRadius: "50%"
                    }}
                  />
                </Box>
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Content>
    </Layout>
  );
};

export default CapturaResultados;
